import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Gavel from '@mui/icons-material/Gavel';
import Groups from '@mui/icons-material/Groups';
import Numbers from '@mui/icons-material/Numbers';
import Settings from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import Warning from '@mui/icons-material/Warning';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    ListSubheader,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { Fragment } from 'react';

import NumeroProcesso from '@/components/NumeroProcesso';
import PainelFiltros from '@/components/PainelFiltros';
import FiltroRapido from '@/components/sessao/FiltroRapido';
import ModalImpressao from '@/components/shared/ModalImpressao';
import ErrorUtil from '@/util/ErrorUtil';
import HtmlDiff from '@/util/HtmlDiff';
import NomeUtil from '@/util/NomeUtil';
import NumeroProcessoUtil from '@/util/NumeroProcessoUtil';
import StringUtil from '@/util/StringUtil';

import ReactComponent from '../../../ReactComponent';
import SituacaoAcordao from '../../SituacaoAcordao';

const ESTILO_BORDA = '1px solid rgba(224, 224, 224, 1)';
const LARGURA_COLUNA_TIPO = '160px';
const LARGURA_COLUNA_CONTEUDO = '300px';

const TEXT_STYLES = {
    fontSize: '14px',
    lineHeight: '20px'
};

const TEXT_STYLES_MULTILINHA = {
    ...TEXT_STYLES,
    whiteSpace: 'pre-line'
};

/*
 * Estilo do bloco de dados brutos do cadastro — fonte menor, cinza, para
 * diferenciar visualmente do texto provável que aparece logo abaixo na
 * mesma célula.
 */
const TEXT_STYLES_BRUTO = {
    fontSize: '11px',
    lineHeight: '16px',
    color: grey[600],
    whiteSpace: 'pre-line'
};

/*
 * Estilo dos avisos amarelos: marcador pluralizador malformado, divergência
 * de gênero.
 */
const TEXT_STYLES_AVISO = {
    fontSize: 12,
    color: 'warning.main',
    fontStyle: 'italic',
    fontWeight: 'bold'
};

/*
 * Rótulo curto de cada modo de agrupamento, usado para rotular as
 * variantes de texto provável exibidas na coluna Fonte.
 */
const ROTULOS_MODO_AGRUPAMENTO = {
    denominacao: 'denominação',
    polo: 'polo',
    parte: 'parte'
};

/*
 * Cor e tamanho do selo de "coincide".
 */
const COR_COINCIDE = green[700];
const TAMANHO_ICONE_COINCIDE = 18;

const TEXTO_VAZIO = '—';

/*
 * Comparação de partes — estratégia
 * ==================================
 *
 * As denominações processuais chegam em formatos imprevisíveis:
 *
 *   · coladas com marcador pluralizador em posição arbitrária
 *     (`Agravante(s)`, `Recorrido(a)(s)S`);
 *   · compostas por várias denominações unidas por `, ` ou ` e `;
 *   · com flexão de gênero e número variável;
 *   · multi-palavra (`Litisconsorte Necessário`, `Autoridade Coatora`).
 *
 * O parser do voto caminha token a token a partir do verbo (`é` / `são`),
 * e em cada posição tenta reconhecer a MAIOR denominação conhecida possível
 * (até MAX_PALAVRAS_DENOMINACAO) contra o dicionário DENOMINACOES_NORMALIZADAS.
 *
 * Dicionário:
 *   DENOMINACOES_PARTES_CONHECIDAS contém a lista extraída do cadastro do
 *   banco de dados. Para cada entrada, geramos variações automáticas de
 *   plural e gênero.
 *
 * Gênero presumido da parte:
 *   1) nome começa com termo da lista TERMOS_PESSOA_JURIDICA_MASCULINA
 *      (ex.: `BANCO`, `INSTITUTO`, `GRUPO`, `SINDICATO`/`SIND`) →
 *      masculino. A normalização troca pontuação por ESPAÇO para que
 *      abreviações como `SIND.` produzam o token `SIND` (sem isso,
 *      `SIND.CAT.PROFISS...` viraria um único token colado e nunca
 *      casaria com o termo esperado);
 *   2) `tipoPessoa === 'JURIDICA'` → feminino;
 *   3) nome tem marca textual de pessoa jurídica → feminino;
 *   4) prenome termina em TERMINACOES_FEMININAS → feminino;
 *   5) demais casos → masculino.
 * O gênero do GRUPO é feminino apenas se TODAS as partes do grupo forem
 * femininas.
 *
 * Coluna Fonte da linha Partes — três camadas:
 *   1) "Dados do cadastro": a string de denominação original, exatamente
 *      como veio do JSON, acompanhada de nome e tipo de pessoa. O nome é
 *      o ORIGINAL — nunca abreviado, mesmo sob segredo de justiça.
 *   2) "Texto provável (principal)": reconstrução que imita o estilo do
 *      voto, no modo escolhido como mais próximo do texto bruto.
 *   3) "Texto provável alternativo": quando outros modos geram textos
 *      diferentes do principal, exibimos cada um em cinza claro, com
 *      rótulo próprio, para ajudar a identificar qual estratégia o autor
 *      usou.
 *
 * Modos de reconstrução do texto provável:
 *   · modo `denominacao` (padrão): agrupa por CONJUNTO de denominações,
 *     sem distinguir polo. Cada parte entra UMA vez, no grupo cujo
 *     conjunto de denominações coincide com o seu.
 *   · modo `polo`: agrupa por (POLO + CONJUNTO de denominações).
 *   · modo `parte`: agrupa por NOME da parte, reunindo TODAS as
 *     denominações daquela parte. Necessário quando a mesma parte aparece
 *     com denominações diferentes em grupos distintos (ex.: a mesma pessoa
 *     é AGRAVANTE e RECORRENTE) — o cadastro repete a parte, e o voto a
 *     menciona uma única vez com `e` ligando as denominações
 *     (`é AGRAVANTE e RECORRENTE FULANO`). Este modo reproduz essa forma.
 *
 *   NUNCA fazemos união de denominações diferentes no mesmo grupo nos modos
 *   `denominacao` / `polo` — isso produziria textos inválidos (ex.:
 *   `Recorrente` e `Recorrido` aplicados à mesma parte). Cada grupo tem um
 *   conjunto ÚNICO de denominações. O modo `parte` é a exceção deliberada:
 *   ele agrupa por NOME, e por isso exibe as denominações ligadas por `e`.
 *
 *   Os três modos produzem textos diferentes apenas quando:
 *     · `polo` difere de `denominacao` → quando um MESMO conjunto de
 *       denominações aparece em polos distintos;
 *     · `parte` difere de `denominacao` → quando uma mesma parte aparece
 *       em grupos de denominações diferentes.
 *
 *   ORDEM DO TEXTO PROVÁVEL = ORDEM DO VOTO:
 *   Para que diferenças meramente de ORDEM não gerem diff, o texto provável
 *   é reconstruído seguindo a ordem em que os nomes aparecem no VOTO bruto:
 *     · dentro de cada grupo, os nomes são reordenados conforme a posição
 *       em que aparecem no voto; nomes que o voto não tem ficam no fim,
 *       na ordem original do cadastro;
 *     · os grupos são emitidos na ordem de primeira aparição dos seus
 *       nomes no voto; grupos com nomes ausentes no voto vão para o fim.
 *
 *   ORDEM DAS DENOMINAÇÕES DENTRO DO GRUPO = ORDEM DO CADASTRO:
 *   Em vez de ordenar alfabeticamente, preservamos a ordem em que as
 *   denominações aparecem no cadastro. O autor da minuta costuma seguir
 *   essa ordem no voto (`Agravante(s) e Agravado(s)S`), e a ordenação
 *   alfabética produziria uma divergência artificial de ordem.
 *
 *   A ordenação alfabética é usada APENAS para construir a chave do grupo,
 *   garantindo que partes com o mesmo conjunto em ordens diferentes caiam
 *   no mesmo balde. A exibição usa a ordem do primeiro membro do grupo.
 *
 *   POSIÇÃO RESPEITA O GRUPO:
 *   Quando um mesmo nome aparece em MAIS DE UM segmento do voto (ex.:
 *   `FULANO` em `AGRAVANTES FULANO` e também em `AGRAVADOS FULANO`), uma
 *   posição GLOBAL baseada na primeira ocorrência leva a ordem errada —
 *   o nome cai no grupo do voto em que apareceu primeiro, embaralhando os
 *   demais. Por isso a posição é resolvida em DUAS camadas:
 *     1) `(assinatura das denominações do grupo) + nome` — casamento exato
 *        com a posição da parte no segmento correspondente do voto;
 *     2) `nome` — fallback global, usado quando o grupo não tem
 *        correspondente direto no voto (caso típico do modo `parte`).
 *
 *   A chave usada para casar nomes entre cadastro e voto é
 *   `chaveDeComparacaoDeNome` — ignora caixa, acento e PONTUAÇÃO. Sem
 *   essa tolerância, nomes como `CLARO S.A.` (voto) e `CLARO SA` (cadastro)
 *   ou `COMUNICAÇÕES` (voto) e `COMUNICACOES` (cadastro) não casariam, e
 *   a ordenação do grupo cairia para a ordem do cadastro — gerando diff
 *   de ordem espúrio.
 *
 *   Diferenças reais (nome faltando, denominação divergente, plural,
 *   gênero) continuam aparecendo em verde/vermelho.
 *
 *   ESCOLHA DA VARIANTE PRINCIPAL:
 *   Os três modos rodam e produzem textos. A variante principal é
 *   escolhida em dois critérios, nesta ordem:
 *     1) DIVERGÊNCIA VERBAL — |singularVoto − singularVariante| +
 *        |pluralVoto − pluralVariante|. O voto do relator escolhe entre
 *        a forma singular (`é X`) e a plural (`são X e Y`) conforme o
 *        agrupamento adotado. Uma variante cuja contagem de verbos bate
 *        com o voto tem muito mais chance de refletir a estrutura
 *        pretendida.
 *     2) DIFF TEXTUAL — menor número de `ins` + `del` no diff contra o
 *        voto bruto.
 *
 *   Sem o critério (1), o modo `parte` (que sempre produz verbos no
 *   singular, porque cada grupo tem 1 nome) podia vencer por acaso quando
 *   o diff bruto ficava ligeiramente menor — mesmo em votos estruturados
 *   claramente como `são Agravante(s) e Agravado(s)S NOME1 e NOME2`.
 *
 *   As demais variantes (que produzem textos diferentes) viram
 *   alternativas exibidas na coluna Fonte.
 *
 * Segredo de justiça:
 *   Quando o processo tem `segredoJustica: true`, o TEXTO PROVÁVEL é
 *   gerado com os nomes abreviados via `NomeUtil.abreviarNome`. A
 *   abreviação acontece onde o nome alimenta o agrupamento/ordenação —
 *   `getNomeParaComparacao` —, NUNCA na exibição de "Dados do cadastro".
 *
 *   As DENOMINAÇÕES continuam sendo AGRUPADAS normalmente sob segredo de
 *   justiça — o NÚMERO (plural) é aplicado, porque a contagem de nomes no
 *   grupo é um dado OBSERVÁVEL do cadastro, não uma inferência. O que NÃO
 *   se aplica é a FLEXÃO DE GÊNERO: sob segredo os nomes vêm abreviados
 *   (siglas/iniciais), e o gênero real da parte não pode ser inferido —
 *   feminilizar seria uma invenção.
 *
 *   Assim:
 *     · `são Agravantes E.B.C.T.E. e U.F.M.G.`  — plural OK;
 *     · `são Agravadas E.B.C.T.E. e U.F.M.G.`   — NÃO (gênero bloqueado).
 *
 *   Separação de responsabilidades:
 *     · `ParteProcessual.nome`  — nome ORIGINAL, como veio do JSON.
 *       Consumido por `formatarPartesBrutas` (bloco "Dados do cadastro"),
 *       que é uma coluna de auditoria e deve mostrar o que o cadastro
 *       realmente tem.
 *     · `ParteProcessual.segredoJustica` — flag copiada para a parte.
 *     · `getNomeParaComparacao(parte)` — helper que aplica a abreviação
 *       quando `segredoJustica === true`. Consumido por `agruparPartes*`,
 *       `montarGrupoDePartes`, `construirMapasDePosicao` (lado do
 *       cadastro) e `formatarPartesNormalizado`.
 *
 *   O flag `segredoJustica` é procurado em MÚLTIPLOS NÍVEIS, porque
 *   diferentes formatos de payload o colocam em lugares distintos:
 *     · no próprio registro de análise (`registro.segredoJustica`);
 *     · no objeto do processo (`registro.processo.segredoJustica`);
 *     · aninhado em `registro.processo.processo.segredoJustica`;
 *     · no processo vindo da sessão (`sessaoStore...processos[i].processo.segredoJustica`);
 *     · como fallback, na entrada do `mapaProcessosPartes` — que em
 *       formatos legados é apenas um array sem o flag.
 *   A primeira ocorrência `true` vence.
 *
 *   Presume-se que o voto bruto também esteja anonimizado pelo mesmo
 *   critério; do contrário, a comparação acusa divergências que são, na
 *   verdade, efeito da anonimização. O sistema não tenta reconstruir o
 *   nome completo — apenas exibe o que cada lado traz.
 *
 * Sinais de alerta:
 *   1) Marcador pluralizador malformado (`Recorrido(s)S`, `Agravado(s)ES`).
 *   2) Divergência de flexão de gênero.
 *
 * Contador de processos — três números distintos:
 *   · seqFixo              — posição do processo na lista filtrada por busca.
 *   · totalProcessosConsulta — total retornado pela consulta original.
 *   · seqVisivel           — posição entre os efetivamente exibidos.
 *
 * Filtro de processos por configuração selecionada:
 *   · "Exibir processos com divergências..." / "sem divergências...".
 *   · Sub-filtro "Incluir divergências de morfologia flexional": quando
 *     DESLIGADO, divergências de plural/gênero (no diff e nos avisos)
 *     deixam de contar para filtragem.
 *
 * Estado de erro do carregamento:
 *   Quando `buscarDadosAnaliseSecretaria` falha, a exceção é capturada em
 *   `carregarInformacoesCertidaoAcordao` e comunicada via notistack. A flag
 *   `erroCarregamento` é mantida apenas para que a área de conteúdo NÃO
 *   mostre "Nenhum processo disponível".
 */

const REGEX_PRIMEIRA_DENOMINACAO = /(?:é|são)\s+([\p{L}()]+)/u;

/*
 * Marcador pluralizador: `(s)`, `(a)`, `(es)`, `(as)` — letras dentro de
 * parênteses, eventualmente com espaço interno. Sem `\s*` envolvendo a
 * classe, para evitar backtracking em textos longos com muitos parênteses
 * desbalanceados.
 */
const REGEX_MARCADOR_PLURAL = /\(\s*\p{L}+\s*\)/gu;

const REGEX_MARCADOR_PLURAL_MALFORMADO = /\(\p{L}+\)\p{L}/u;

const REGEX_SEPARADOR_TRECHOS = /(?:\s+e\s+|\s*,\s*)(?=(?:é|são)\s+)/i;

const REGEX_INICIO_TRECHO_SIMPLES = /^(é|são)\s+(.+)$/isu;

const REGEX_SEPARADOR_DENOMINACOES = /\s*,\s*|\se\s+/i;

const REGEX_SEPARADOR_NOMES_PLURAL = /\s*,\s*|\se\s+/;

const REGEX_PARTES_DO_NOME = /\s+/;

const REGEX_PONTUACAO_FINAL = /[.,;:]+$/;

const REGEX_PONTUACAO_GERAL = /[^A-Z0-9\s]/g;

/*
 * Substitui QUALQUER caractere que não seja letra ou número por um espaço.
 * Usada onde precisamos preservar a segmentação em tokens — por exemplo,
 * `SIND.CAT.PROFISS.EMPREG...` precisa produzir o token `SIND`, e não o
 * token colado `SINDCATPROFISS...`. Diferente de REGEX_PONTUACAO_GERAL,
 * que simplesmente remove a pontuação.
 */
const REGEX_SEPARADORES_PARA_ESPACO = /[^\p{L}\p{N}]+/gu;

const REGEX_TOKEN_CONECTOR = /^[eE,;.]+$/;

const REGEX_TERMINACAO_MASCULINA = /OS$|O$/;

const REGEX_TERMINACAO_FEMININA = /AS$|A$/;

const REGEX_TERMINACAO_PLURAL_CLARA = /(?:OS|AS|ES|US)$/;

const SUFIXOS_PESSOA_JURIDICA = new Set(['LTDA', 'SA', 'EIRELI', 'EPP', 'ME', 'MEI']);

const TERMOS_INDICADORES_PESSOA_JURIDICA = new Set([
    'BANCO',
    'COMPANHIA',
    'CIA',
    'EMPRESA',
    'SOCIEDADE',
    'ASSOCIACAO',
    'FUNDACAO',
    'INSTITUTO',
    'COOPERATIVA',
    'GRUPO'
]);

/*
 * Termos de pessoa jurídica cujo gênero gramatical é MASCULINO. Precisam
 * vencer a regra "pessoa jurídica → feminino" em `detectarGeneroDaParte`,
 * porque nem toda PJ é feminina: `o sindicato`, `o banco`, `o instituto`
 * são masculinos, enquanto `a empresa`, `a companhia`, `a sociedade` são
 * femininos. `SIND` cobre a abreviação `SIND.` usada em alguns cadastros.
 */
const TERMOS_PESSOA_JURIDICA_MASCULINA = new Set(['BANCO', 'INSTITUTO', 'GRUPO', 'SINDICATO', 'SIND']);

const DENOMINACOES_PARTES_CONHECIDAS = [
    'Consulente',
    'Corregedor-Geral',
    'Correicionado',
    'Inspecionado',
    'Administrador Judicial',
    'Amicus Curiae',
    'Agravante',
    'Agravado',
    'Recorrente',
    'Recorrido',
    'Embargante',
    'Embargado',
    'Impetrante',
    'Impetrado',
    'Arrematante',
    'Proponente',
    'Consultado',
    'Testemunha',
    'Ordenante',
    'Ordenado',
    'Arguinte',
    'Arguido',
    'Conjuge Supérstite',
    'Autor',
    'Réu',
    'Ré',
    'Requerente',
    'Requerido',
    'Suscitante',
    'Suscitado',
    'Autoridade Coatora',
    'Litisconsorte Necessário',
    'Remetente',
    'Interessado',
    'Excipiente',
    'Excepto',
    'Assistente',
    'Impugnante',
    'Impugnado',
    'Reclamante',
    'Reclamado',
    'Paciente',
    'Exequente',
    'Executado',
    'Assistente Litisconsorcial',
    'Representante',
    'Representado',
    'Acusado',
    'Indiciado',
    'Terceiro Interessado',
    'Opoente',
    'Oposto',
    'Litisconsorte',
    'Síndico',
    'Interveniente',
    'Litisconsorte Passivo Necessário',
    'Litisconsorte Ativo',
    'Litisconsorte Passivo',
    'Assistente Simples',
    'Administrador',
    'Custos Legis',
    'Inventariante',
    'Sindicante',
    'Sindicado',
    'Perito'
];

/**
 * Normaliza um texto: remove acentos, força caixa alta, colapsa espaços.
 *
 * @param {string} texto
 * @returns {string}
 */
function normalizarTexto(texto) {
    return String(texto)
        .normalize('NFD')
        .replaceAll(/[\u{300}-\u{36F}]/gu, '')
        .toUpperCase()
        .replaceAll(/\s+/g, ' ')
        .trim();
}

/**
 * Chave canônica usada APENAS para casar nomes entre cadastro e voto
 * durante a reconstrução do texto provável.
 *
 * Diferenças em relação a `normalizarNome`:
 *   · remove PONTUAÇÃO (`.`, `,`, `/`, `-`, `&`, `'` etc.) — porque o voto
 *     costuma trazer `S.A.` / `LTDA.` com ponto final, enquanto o cadastro
 *     costuma trazer `SA` / `LTDA` sem;
 *   · remove ACENTOS — porque o voto pode trazer `COMUNICAÇÕES` e o
 *     cadastro `COMUNICACOES` para a mesma parte.
 *
 * Sem essa tolerância, o nome não casa, cai para `Infinity` na ordenação,
 * e o diff passa a acusar uma diferença de ORDEM que não é real.
 *
 * NÃO usar para exibição — só para casar/ordenar.
 *
 * @param {string} nome
 * @returns {string}
 */
function chaveDeComparacaoDeNome(nome = '') {
    return normalizarTexto(nome).replaceAll(REGEX_PONTUACAO_GERAL, '').replaceAll(/\s+/g, ' ').trim();
}

/**
 * Normaliza uma denominação para uso como chave de agrupamento/assinatura.
 *
 * Aplica: caixa alta, remoção de acento, remoção de marcador pluralizador,
 * singularização e neutralização de gênero (via masculinização). Assim,
 * `AGRAVANTE`/`AGRAVANTES` viram `AGRAVANTE`, e `AGRAVADO`/`AGRAVADA`/
 * `AGRAVADOS`/`AGRAVADAS` viram `AGRAVADO`.
 *
 * Denominações com papéis diferentes (`AGRAVANTE` × `AGRAVADO`) continuam
 * distintas — só a flexão é neutralizada.
 *
 * @param {string} d
 * @returns {string}
 */
function normalizarDenominacaoParaChave(d = '') {
    return masculinizarPalavra(singularizarPalavra(normalizarTexto(removerMarcadores(d))));
}

/**
 * Assinatura canônica de um conjunto de denominações: normaliza cada uma,
 * ordena alfabeticamente e junta por `|`. Usada para casar um grupo do
 * cadastro com uma parte do voto.
 *
 * @param {string[]} denominacoes
 * @returns {string}
 */
function assinaturaDenominacoes(denominacoes = []) {
    return (denominacoes || [])
        .map((d) => normalizarDenominacaoParaChave(d))
        .toSorted((a, b) => a.localeCompare(b))
        .join('|');
}

/**
 * Remove marcadores pluralizadores em qualquer posição do texto.
 *
 * @param {string} texto
 * @returns {string}
 */
function removerMarcadores(texto) {
    return String(texto).replaceAll(REGEX_MARCADOR_PLURAL, '').replaceAll(/\s+/g, ' ').trim();
}

/**
 * Singulariza uma palavra.
 *
 * @param {string} p
 * @returns {string}
 */
function singularizarPalavra(p) {
    if (p.length > 2 && (p.endsWith('OS') || p.endsWith('AS') || p.endsWith('ES'))) {
        return p.slice(0, -1);
    }
    return p;
}

/**
 * Pluraliza uma palavra segundo as regras usuais do português.
 *
 * @param {string} p
 * @returns {string}
 */
function pluralizarPalavra(p) {
    if (!p) return p;
    const upper = p.toUpperCase();

    if (upper.endsWith('S')) return p;
    if (upper.endsWith('R') || upper.endsWith('Z')) return p + 'ES';
    if (upper.endsWith('M')) return p.slice(0, -1) + 'NS';
    if (upper.endsWith('L')) return p.slice(0, -1) + 'IS';
    if ('AEIOU'.includes(upper.slice(-1))) return p + 'S';
    return p;
}

/**
 * Feminiza uma palavra terminada em `O`/`OS`.
 *
 * @param {string} p
 * @returns {string}
 */
function feminizarPalavra(p) {
    const upper = p.toUpperCase();

    if (upper.endsWith('OS')) return p.slice(0, -1) + 'AS';
    if (upper.endsWith('O')) return p.slice(0, -1) + 'A';
    return p;
}

/**
 * Masculiniza uma palavra terminada em `A`/`AS`.
 *
 * @param {string} p
 * @returns {string}
 */
function masculinizarPalavra(p) {
    const upper = p.toUpperCase();

    if (upper.endsWith('AS')) return p.slice(0, -1) + 'OS';
    if (upper.endsWith('A')) return p.slice(0, -1) + 'O';
    return p;
}

/**
 * Gera variantes normalizadas de uma frase.
 *
 * @param {string} frase
 * @returns {string[]}
 */
function gerarVariantesDeFrase(frase) {
    const semMarcador = removerMarcadores(frase);
    const normalizada = normalizarTexto(semMarcador);

    if (!normalizada) return [];

    const palavras = normalizada.split(' ');

    const combos = [
        palavras,
        palavras.map((palavra) => singularizarPalavra(palavra)),
        palavras.map((palavra) => pluralizarPalavra(palavra)),
        palavras.map((palavra) => feminizarPalavra(palavra)),
        palavras.map((p) => feminizarPalavra(singularizarPalavra(p))),
        palavras.map((p) => feminizarPalavra(pluralizarPalavra(p))),
        palavras.map((p) => masculinizarPalavra(singularizarPalavra(p))),
        palavras.map((p) => masculinizarPalavra(pluralizarPalavra(p)))
    ];

    const variantes = new Set();

    for (const combo of combos) {
        variantes.add(combo.join(' '));
    }
    return [...variantes];
}

/*
 * Constrói o Set com todas as variantes normalizadas das denominações.
 */
const DENOMINACOES_NORMALIZADAS = new Set();

for (const raw of DENOMINACOES_PARTES_CONHECIDAS) {
    for (const variante of gerarVariantesDeFrase(raw)) {
        DENOMINACOES_NORMALIZADAS.add(variante);
    }
}

const MAX_PALAVRAS_DENOMINACAO = 4;

const TERMINACOES_FEMININAS = ['NICE'];

/**
 * @typedef {object} ParteProcessual
 * @property {string} [denominacaoOriginal]
 * @property {string[]} denominacoes
 * @property {string} nome          nome ORIGINAL, como veio do JSON
 * @property {boolean} [segredoJustica]  quando true, `getNomeParaComparacao`
 *                                  devolve a versão abreviada via NomeUtil
 * @property {string} [tipoPolo]
 * @property {number} [ordem]
 * @property {string} [cpfCnpj]
 * @property {string} [tipoPessoa]
 * @property {number} [codigo]
 */

/**
 * @typedef {object} GrupoDePartes
 * @property {string[]} denominacoes   conjunto de denominações — ordem do
 *                                     cadastro (primeiro membro do grupo)
 * @property {string[]} nomes          nomes das partes do grupo
 * @property {string[]} generos        gênero presumido de cada nome (mesma ordem)
 * @property {boolean} segredoJustica  quando true, as denominações são
 *                                     flexionadas apenas em NÚMERO (plural);
 *                                     a flexão de GÊNERO é bloqueada
 */

/**
 * @typedef {object} VarianteTexto
 * @property {'denominacao'|'polo'|'parte'} modo
 * @property {string} texto
 */

/**
 * @typedef {object} Comparacao
 * @property {'classeNumero'|'decisao'|'partes'} tipo
 * @property {string} label
 * @property {'certidao'|'cadastro'} fonte
 * @property {React.ReactNode} conteudoFonte
 * @property {React.ReactNode} conteudoVoto
 * @property {React.ReactNode} comparacaoNode
 * @property {boolean} temAlteracoes
 */

class ModalRelatorioComparacaoCertidaoAcordao extends ReactComponent {
    /** @type {boolean} */
    isCarregando = true;

    /** @type {boolean} */
    erroCarregamento = false;

    /** @type {boolean} */
    configuracoesAbertas = false;

    /** @type {boolean} */
    exibirProcessosSemDivergencias = true;

    /** @type {boolean} */
    exibirProcessosComDivergencias = true;

    /** @type {boolean} */
    incluirDivergenciasFlexionais = true;

    /** @type {boolean} */
    exibirComparacaoClasseNumero = true;

    /** @type {boolean} */
    exibirComparacaoDecisao = true;

    /** @type {boolean} */
    exibirComparacaoPartes = true;

    /** @type {Array} */
    informacoesCertidaoAcordao = [];

    iniciarComponente = () => {
        this.carregarInformacoesCertidaoAcordao();
    };

    atualizarIsCarregando = (isCarregando) => {
        this.isCarregando = isCarregando;
    };

    registrarErroCarregamento = (temErro) => {
        this.erroCarregamento = temErro;
    };

    handleToggleConfiguracoes = () => {
        this.configuracoesAbertas = !this.configuracoesAbertas;
    };

    handleChangeExibirProcessosSemDivergencias = () => {
        this.exibirProcessosSemDivergencias = !this.exibirProcessosSemDivergencias;
    };

    handleChangeExibirProcessosComDivergencias = () => {
        this.exibirProcessosComDivergencias = !this.exibirProcessosComDivergencias;
    };

    handleChangeIncluirDivergenciasFlexionais = () => {
        this.incluirDivergenciasFlexionais = !this.incluirDivergenciasFlexionais;
    };

    handleChangeExibirComparacaoClasseNumero = () => {
        this.exibirComparacaoClasseNumero = !this.exibirComparacaoClasseNumero;
    };

    handleChangeExibirComparacaoDecisao = () => {
        this.exibirComparacaoDecisao = !this.exibirComparacaoDecisao;
    };

    handleChangeExibirComparacaoPartes = () => {
        this.exibirComparacaoPartes = !this.exibirComparacaoPartes;
    };

    limparProcessoPesquisa = () => {
        this.processoPesquisa = {
            numero: '',
            digito: '',
            ano: '',
            orgao: '',
            regional: '',
            vara: ''
        };
    };

    getDataGeracao = () => {
        return 'Gerado em ' + moment().format('DD/MM/YYYY HH:mm');
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            montarComparacoes: true,
            montarComparacaoClasseNumero: true,
            montarComparacaoDecisao: true,
            montarComparacaoPartes: true,
            getProcessoPorNumero: true,
            buscarPartesProcesso: true,
            detectarSegredoJustica: true,
            extrairEntradaDePartes: true,
            extrairListaDePartes: true,
            getNomeParaComparacao: true,
            extrairPartesDoCadastro: true,
            parsePartesDoVoto: true,
            parseTrechoDoVoto: true,
            identificarDenominacoesNoTrecho: true,
            matchDenominacaoEm: true,
            dividirDenominacoesCompostas: true,
            limparTokenDeDenominacao: true,
            votoTemMarcadorPluralMalformado: true,
            extrairMarcasDeGenero: true,
            detectarGeneroDoToken: true,
            normalizarBaseDaDenominacao: true,
            textosTemDivergenciaDeGenero: true,
            nomeTemMarcaDePessoaJuridica: true,
            nomeIndicaPessoaJuridicaMasculina: true,
            ePessoaJuridica: true,
            normalizarNome: true,
            gerarVariantesDeTextoPartes: true,
            contarVerbosNoTexto: true,
            montarTextoPartesDoCadastro: true,
            agruparPartesParaTexto: true,
            agruparPartesPorNome: true,
            construirMapasDePosicao: true,
            montarGrupoDePartes: true,
            ordenarNomesPelaReferencia: true,
            escolherMelhorVariante: true,
            formatarPartesNormalizado: true,
            normalizarDenominacaoParaDiff: true,
            formatarDenominacao: true,
            removerMarcadorPlural: true,
            aplicarCaixa: true,
            paraTitulo: true,
            pluralizarDenominacao: true,
            aplicarGeneroSingular: true,
            detectarGeneroDaParte: true,
            extrairPrenome: true,
            detectarCaixaDenominacaoVoto: true,
            juntarComConjuncao: true,
            isComparacaoHabilitada: true,
            getRotuloFonte: true,
            renderConfiguracoes: true,
            renderColunaSwitch: true,
            renderVereditoComparacao: true,
            getDataGeracao: true,
            getInfoSituacaoAcordao: true,
            renderTableHead: true,
            calcularEstatisticasMudancasTexto: false,
            renderTableBody: true,
            renderMsgAlerta: true,
            renderConteudo: true,
            render: true,
            renderTextoSolto: true,
            renderConteudoFontePartes: true,
            formatarPartesBrutas: true,

            isCarregando: observable,
            erroCarregamento: observable,
            configuracoesAbertas: observable,
            exibirProcessosSemDivergencias: observable,
            exibirProcessosComDivergencias: observable,
            incluirDivergenciasFlexionais: observable,
            exibirComparacaoClasseNumero: observable,
            exibirComparacaoDecisao: observable,
            exibirComparacaoPartes: observable,
            informacoesCertidaoAcordao: observable,

            numeroProcessosFiltrados: computed,
            informacoesCertidaoAcordaoFiltradas: computed,
            processosVisiveis: computed,
            totalProcessosConsulta: computed,
            titulo: computed,
            cabecalho: computed,
            isAindaCarregando: computed,

            iniciarComponente: action,
            atualizarIsCarregando: action,
            registrarErroCarregamento: action,
            carregarInformacoesCertidaoAcordao: action,
            handleToggleConfiguracoes: action,
            handleChangeExibirProcessosSemDivergencias: action,
            handleChangeExibirProcessosComDivergencias: action,
            handleChangeIncluirDivergenciasFlexionais: action,
            handleChangeExibirComparacaoClasseNumero: action,
            handleChangeExibirComparacaoDecisao: action,
            handleChangeExibirComparacaoPartes: action,
            limparProcessoPesquisa: action
        });

        this.iniciarComponente();
    }

    get numeroProcessosFiltrados() {
        return (this.store.processosFiltrados || [])
            .filter((registro) => this.processoPesquisa && this.filtrarProcessoPeloNumero(registro))
            .map((registro) => registro.numeroProcesso);
    }

    get isAindaCarregando() {
        return this.isCarregando || this.salaSessaoStore?.carregandoPartes === true;
    }

    get totalProcessosConsulta() {
        return this.informacoesCertidaoAcordao?.length ?? 0;
    }

    async carregarInformacoesCertidaoAcordao() {
        this.atualizarIsCarregando(true);
        this.registrarErroCarregamento(false);

        try {
            const respostaCertidoesAcordaos = await this.sessaoService.buscarDadosAnaliseSecretaria(
                this.sessaoStore.sessaoSelecionada.id
            );

            runInAction(() => {
                this.informacoesCertidaoAcordao = respostaCertidoesAcordaos;
            });
        } catch (error) {
            console.error(
                `Falha ao carregar informações do ModalRelatorioComparacaoCertidaoAcordao: ${ErrorUtil.getMensagemErro(error)}`
            );

            this.registrarErroCarregamento(true);

            this.mensagemStore.abrirMensagemErro('Erro ao carregar informações de partes, votos e certidões.');
        } finally {
            this.atualizarIsCarregando(false);
        }
    }

    get informacoesCertidaoAcordaoFiltradas() {
        let index = 0;

        return this.informacoesCertidaoAcordao
            .filter((registro) =>
                this.numeroProcessosFiltrados.includes(
                    NumeroProcessoUtil.removerClasseProcessual(registro.numeroProcesso)
                )
            )
            .map((registro) => {
                const comparacoes = this.montarComparacoes(registro);

                return {
                    seq: ++index,
                    numeroProcesso: registro.numeroProcesso,
                    comparacoes
                };
            });
    }

    get processosVisiveis() {
        const lista = this.informacoesCertidaoAcordaoFiltradas || [];
        const visiveis = [];
        let seqVisivel = 0;

        for (const item of lista) {
            const comparacoesVisiveis = item.comparacoes.filter((c) => this.isComparacaoHabilitada(c.tipo));
            const temDivergenciasVisiveis = comparacoesVisiveis.some((c) => c.temAlteracoes);

            if (temDivergenciasVisiveis && !this.exibirProcessosComDivergencias) continue;
            if (!temDivergenciasVisiveis && !this.exibirProcessosSemDivergencias) continue;

            seqVisivel += 1;

            visiveis.push({
                seqFixo: item.seq,
                seqVisivel,
                numeroProcesso: item.numeroProcesso,
                comparacoesVisiveis
            });
        }

        return visiveis;
    }

    montarComparacoes(registro) {
        return [
            this.montarComparacaoClasseNumero(registro),
            this.montarComparacaoDecisao(registro),
            this.montarComparacaoPartes(registro)
        ];
    }

    /**
     * Comparação 1) — Classe e número.
     *
     * @param {object} registro
     * @returns {Comparacao}
     */
    montarComparacaoClasseNumero(registro) {
        const classeNumeroFonte = NumeroProcessoUtil.formatarNumeroProcesso(
            registro.processo?.numeroCompleto ?? registro.numeroProcesso ?? '',
            true,
            true
        );
        const classeNumeroVoto = NumeroProcessoUtil.formatarNumeroProcesso(
            registro.analiseSecretariaVoto?.numeroProcessoVoto ?? '',
            true,
            true
        );
        const diffClasseNumero = HtmlDiff.execute(classeNumeroFonte, classeNumeroVoto);
        const statsClasseNumero = this.calcularEstatisticasMudancasTexto(diffClasseNumero);
        const isClasseNumeroCoincide = statsClasseNumero.totalInsercoes === 0 && statsClasseNumero.totalExclusoes === 0;
        const isClasseNumeroVazio = !classeNumeroFonte && !classeNumeroVoto;

        return {
            tipo: 'classeNumero',
            label: 'Classe e número',
            fonte: 'cadastro',
            conteudoFonte: this.renderTextoSolto(classeNumeroFonte),
            conteudoVoto: this.renderTextoSolto(classeNumeroVoto),
            comparacaoNode: this.renderVereditoComparacao({
                diffHtml: diffClasseNumero,
                temAlteracoes: !isClasseNumeroCoincide,
                mensagemCoincide: 'Classe e número coincidem',
                ambosVazios: isClasseNumeroVazio
            }),
            temAlteracoes: !isClasseNumeroCoincide
        };
    }

    /**
     * Comparação 2) — Decisão.
     *
     * @param {object} registro
     * @returns {Comparacao}
     */
    montarComparacaoDecisao(registro) {
        const decisaoCertidao = StringUtil.tratarCaracteresNaoLatin1(
            registro.analiseSecretariaCertidao?.decisaoCertidao
        );
        const decisaoVoto = this.salaSessaoStore.retirarInicioDecisao(
            StringUtil.tratarCaracteresNaoLatin1(registro.analiseSecretariaVoto?.decisaoVoto)
        );
        const diffDecisao = HtmlDiff.execute(decisaoCertidao ?? '', decisaoVoto ?? '');
        const statsDecisao = this.calcularEstatisticasMudancasTexto(diffDecisao);
        const isDecisaoCoincide = statsDecisao.totalInsercoes === 0 && statsDecisao.totalExclusoes === 0;
        const isDecisaoVazia = !decisaoCertidao && !decisaoVoto;

        return {
            tipo: 'decisao',
            label: 'Decisão',
            fonte: 'certidao',
            conteudoFonte: this.renderTextoSolto(decisaoCertidao),
            conteudoVoto: this.renderTextoSolto(decisaoVoto),
            comparacaoNode: this.renderVereditoComparacao({
                diffHtml: diffDecisao,
                temAlteracoes: !isDecisaoCoincide,
                mensagemCoincide: 'Textos coincidem',
                ambosVazios: isDecisaoVazia
            }),
            temAlteracoes: !isDecisaoCoincide
        };
    }

    /**
     * Comparação 3) — Partes.
     *
     * Reconstruímos o texto provável seguindo a ORDEM em que os nomes
     * aparecem no voto — assim, diferenças meramente de ordem não geram
     * diff. Ver o JSDoc de `montarTextoPartesDoCadastro` para detalhes.
     *
     * @param {object} registro
     * @returns {Comparacao}
     */
    montarComparacaoPartes(registro) {
        const textoVoto = registro.analiseSecretariaVoto?.identificacaoPartesVoto ?? '';
        const partesDoCadastro = this.extrairPartesDoCadastro(registro);
        const partesDoVoto = this.parsePartesDoVoto(textoVoto);

        const variantes = this.gerarVariantesDeTextoPartes(partesDoCadastro, textoVoto, partesDoVoto);
        const { principal, principalModo, diffPartes, alternativos } = this.escolherMelhorVariante(
            variantes,
            textoVoto
        );

        const statsPartes = this.calcularEstatisticasMudancasTexto(diffPartes);
        const isPartesCoincidemExibicao = statsPartes.totalInsercoes === 0 && statsPartes.totalExclusoes === 0;

        const temMarcadorMalformado = this.votoTemMarcadorPluralMalformado(textoVoto);
        const temDivergenciaDeGenero = this.textosTemDivergenciaDeGenero(textoVoto, principal);

        const avisosPartes = [
            temMarcadorMalformado
                ? '⚠ O texto do voto contém marcador pluralizador malformado (ex.: Recorrido(s)S).'
                : null,
            temDivergenciaDeGenero
                ? '⚠ O texto do voto contém marcador de flexão de gênero possivelmente mal aplicado.'
                : null
        ].filter(Boolean);

        const isFallback = Boolean(textoVoto) && partesDoVoto.length === 0;

        let isPartesCoincidemFiltro;

        if (isFallback) {
            isPartesCoincidemFiltro = principal === textoVoto;
        } else if (isPartesCoincidemExibicao) {
            isPartesCoincidemFiltro = true;
        } else if (this.incluirDivergenciasFlexionais) {
            isPartesCoincidemFiltro = false;
        } else {
            const textoFiltroProva = this.formatarPartesNormalizado(partesDoCadastro);
            const textoFiltroVoto = this.formatarPartesNormalizado(partesDoVoto);
            const diffFiltro = HtmlDiff.execute(textoFiltroProva, textoFiltroVoto);
            const statsFiltro = this.calcularEstatisticasMudancasTexto(diffFiltro);

            isPartesCoincidemFiltro = statsFiltro.totalInsercoes === 0 && statsFiltro.totalExclusoes === 0;
        }

        const avisosContamParaFiltro = this.incluirDivergenciasFlexionais;
        const temAlteracoesParaFiltro = !isPartesCoincidemFiltro || (avisosContamParaFiltro && avisosPartes.length > 0);

        const variantesExibir = [
            { modo: principalModo, texto: principal, principal: true },
            ...alternativos.map((v) => ({ modo: v.modo, texto: v.texto, principal: false }))
        ];

        return {
            tipo: 'partes',
            label: 'Partes',
            fonte: 'cadastro',
            conteudoFonte: this.renderConteudoFontePartes(partesDoCadastro, variantesExibir),
            conteudoVoto: this.renderTextoSolto(textoVoto),
            comparacaoNode: (
                <Stack spacing={0.5}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            color: grey[600],
                            fontStyle: 'italic'
                        }}
                    >
                        {isFallback
                            ? 'Texto do voto fora do formato esperado — comparação textual direta'
                            : `Comparação em ordem original (texto base: agrupamento por ${ROTULOS_MODO_AGRUPAMENTO[principalModo]})`}
                    </Typography>
                    {this.renderVereditoComparacao({
                        diffHtml: diffPartes,
                        temAlteracoes: !isPartesCoincidemExibicao,
                        mensagemCoincide: 'Partes coincidem',
                        avisos: avisosPartes,
                        ambosVazios: !textoVoto && !principal
                    })}
                </Stack>
            ),
            temAlteracoes: temAlteracoesParaFiltro
        };
    }

    /**
     * Gera as variantes de texto provável (uma por modo), deduplicadas
     * pelo texto final. A ordem dos candidatos preserva `denominacao`
     * antes de `polo` e `parte` — usada como desempate estável em
     * `escolherMelhorVariante`.
     *
     * @param {ParteProcessual[]} partesDoCadastro
     * @param {string} textoVoto
     * @param {ParteProcessual[]} partesDoVoto
     * @returns {VarianteTexto[]}
     */
    gerarVariantesDeTextoPartes(partesDoCadastro, textoVoto, partesDoVoto) {
        const modos = ['denominacao', 'polo', 'parte'];

        const candidatos = modos.map((modo) => ({
            modo,
            texto: this.montarTextoPartesDoCadastro(partesDoCadastro, textoVoto, modo, partesDoVoto)
        }));

        const vistos = new Set();

        return candidatos.filter((v) => {
            if (!v.texto || vistos.has(v.texto)) return false;
            vistos.add(v.texto);
            return true;
        });
    }

    /**
     * Conta as formas verbais `é` (singular) e `são` (plural) no texto.
     *
     * Critério estrutural em `escolherMelhorVariante`: o voto do relator
     * escolhe entre a forma singular (`é X e Y` — descrevendo as partes
     * uma a uma, agrupamento por parte) e a forma plural (`são X e Y e Z`
     * — descrevendo as partes agrupadas por denominação). Uma variante
     * cuja contagem de verbos coincide com a do voto tem muito mais
     * chance de refletir a estrutura pretendida.
     *
     * A contagem é feita por tokens separados por espaço, com pontuação
     * final removida — assim `é`, `é.` e `é,` contam igual.
     *
     * @param {string} texto
     * @returns {{ singular: number, plural: number }}
     */
    contarVerbosNoTexto(texto = '') {
        const tokens = String(texto).toLowerCase().split(/\s+/);

        let singular = 0;
        let plural = 0;

        for (const token of tokens) {
            const limpo = token.replace(REGEX_PONTUACAO_FINAL, '');

            if (limpo === 'é') singular += 1;
            else if (limpo === 'são') plural += 1;
        }

        return { singular, plural };
    }

    /**
     * Escolhe a variante principal em dois critérios, nesta ordem:
     *
     *   1) DIVERGÊNCIA VERBAL — |singularVoto − singularVariante| +
     *      |pluralVoto − pluralVariante|. O voto do relator escolhe entre
     *      a forma singular e a plural conforme o agrupamento adotado.
     *   2) DIFF TEXTUAL — menor número de `ins` + `del` no diff contra o
     *      voto bruto.
     *
     * Sem o critério (1), o modo `parte` (que sempre produz verbos no
     * singular, porque cada grupo tem 1 nome) podia vencer por acaso
     * quando o diff bruto ficava ligeiramente menor — mesmo em votos
     * claramente estruturados como `são Agravante(s) e Agravado(s)S NOME1
     * e NOME2`.
     *
     * @param {VarianteTexto[]} variantes
     * @param {string} textoVoto
     * @returns {{ principal: string, principalModo: string, diffPartes: string, alternativos: VarianteTexto[] }}
     */
    escolherMelhorVariante(variantes, textoVoto) {
        if (variantes.length === 0) {
            return {
                principal: '',
                principalModo: 'denominacao',
                diffPartes: '',
                alternativos: []
            };
        }

        if (variantes.length === 1) {
            const unica = variantes[0];

            return {
                principal: unica.texto,
                principalModo: unica.modo,
                diffPartes: HtmlDiff.execute(unica.texto, textoVoto ?? ''),
                alternativos: []
            };
        }

        const verbosVoto = this.contarVerbosNoTexto(textoVoto);

        const avaliadas = variantes.map((v) => {
            const diff = HtmlDiff.execute(v.texto, textoVoto ?? '');
            const stats = this.calcularEstatisticasMudancasTexto(diff);
            const verbos = this.contarVerbosNoTexto(v.texto);

            const divergenciaVerbal =
                Math.abs(verbos.singular - verbosVoto.singular) + Math.abs(verbos.plural - verbosVoto.plural);

            return {
                ...v,
                diff,
                mudancas: stats.totalInsercoes + stats.totalExclusoes,
                divergenciaVerbal
            };
        });

        const ordenadas = avaliadas.toSorted((a, b) => {
            // Critério primário: menor divergência no número de verbos.
            if (a.divergenciaVerbal !== b.divergenciaVerbal) {
                return a.divergenciaVerbal - b.divergenciaVerbal;
            }
            // Critério secundário: menor diff textual.
            return a.mudancas - b.mudancas;
        });

        const principal = ordenadas[0];
        const alternativos = ordenadas.slice(1).map(({ modo, texto }) => ({ modo, texto }));

        return {
            principal: principal.texto,
            principalModo: principal.modo,
            diffPartes: principal.diff,
            alternativos
        };
    }

    renderVereditoComparacao({ diffHtml, temAlteracoes, mensagemCoincide, avisos = [], ambosVazios = false }) {
        if (ambosVazios) {
            return this.renderTextoSolto('');
        }

        const temAvisos = avisos.length > 0;
        const cor = temAvisos ? 'warning.main' : COR_COINCIDE;

        return (
            <Stack spacing={0.5}>
                {avisos.map((aviso, index) => (
                    <Typography
                        key={index}
                        sx={TEXT_STYLES_AVISO}
                    >
                        {aviso}
                    </Typography>
                ))}
                {temAlteracoes ? (
                    <Box
                        data-sentry-mask
                        dangerouslySetInnerHTML={{ __html: diffHtml }}
                        sx={TEXT_STYLES_MULTILINHA}
                    />
                ) : (
                    <Stack
                        alignItems="center"
                        direction="row"
                        spacing={0.5}
                    >
                        <CheckCircleOutline
                            sx={{
                                color: cor,
                                fontSize: TAMANHO_ICONE_COINCIDE
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: cor,
                                fontStyle: 'italic'
                            }}
                        >
                            {temAvisos ? `${mensagemCoincide} (com ressalva sinalizada)` : mensagemCoincide}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    }

    votoTemMarcadorPluralMalformado(textoVoto = '') {
        return REGEX_MARCADOR_PLURAL_MALFORMADO.test(textoVoto);
    }

    detectarGeneroDoToken(token = '') {
        const limpo = this.limparTokenDeDenominacao(token).toUpperCase();

        if (!limpo) return null;

        if (REGEX_TERMINACAO_MASCULINA.test(limpo)) return 'masculino';
        if (REGEX_TERMINACAO_FEMININA.test(limpo)) return 'feminino';
        return null;
    }

    normalizarBaseDaDenominacao(texto = '') {
        const limpo = this.limparTokenDeDenominacao(texto).toUpperCase();
        const palavras = limpo.split(/\s+/).filter(Boolean);

        const normalizadas = palavras.map((p) => masculinizarPalavra(singularizarPalavra(p)));
        return normalizadas.join(' ');
    }

    extrairMarcasDeGenero(texto = '') {
        const marcas = new Map();

        if (!texto) return marcas;

        const trechos = texto.split(REGEX_SEPARADOR_TRECHOS);

        for (const trecho of trechos) {
            const match = trecho.trim().match(REGEX_INICIO_TRECHO_SIMPLES);

            if (!match) continue;

            const resto = match[2].trim();
            const tokens = resto.split(/\s+/).filter(Boolean);
            const { denominacoes } = this.identificarDenominacoesNoTrecho(tokens);

            for (const denom of denominacoes) {
                const palavras = denom.texto.split(/\s+/);
                const ultimaPalavra = palavras.at(-1) ?? '';
                const genero = this.detectarGeneroDoToken(ultimaPalavra);

                if (genero) {
                    const base = this.normalizarBaseDaDenominacao(denom.texto);

                    if (!marcas.has(base)) {
                        marcas.set(base, genero);
                    }
                }
            }
        }

        return marcas;
    }

    textosTemDivergenciaDeGenero(textoVoto = '', textoReconstrucao = '') {
        const marcasVoto = this.extrairMarcasDeGenero(textoVoto);
        const marcasReconstrucao = this.extrairMarcasDeGenero(textoReconstrucao);

        for (const [base, generoVoto] of marcasVoto) {
            const generoRec = marcasReconstrucao.get(base);

            if (generoRec && generoRec !== generoVoto) {
                return true;
            }
        }
        return false;
    }

    nomeTemMarcaDePessoaJuridica(nome = '') {
        const normalizado = normalizarTexto(nome).replaceAll(REGEX_PONTUACAO_GERAL, '').replaceAll(/\s+/g, ' ').trim();

        if (!normalizado) return false;

        const palavras = normalizado.split(' ');
        const ultima = palavras.at(-1);

        if (SUFIXOS_PESSOA_JURIDICA.has(ultima)) return true;

        return palavras.some((p) => TERMOS_INDICADORES_PESSOA_JURIDICA.has(p));
    }

    /**
     * Detecta termos de pessoa jurídica cujo gênero gramatical é MASCULINO
     * (`BANCO`, `INSTITUTO`, `GRUPO`, `SINDICATO`/`SIND`...). Precisa vencer
     * a regra padrão "pessoa jurídica → feminino" em `detectarGeneroDaParte`,
     * porque nem toda PJ é feminina — `o sindicato`, `o banco`, `o instituto`
     * são masculinos, enquanto `a empresa`, `a companhia`, `a sociedade` são
     * femininos.
     *
     * A pontuação é substituída por ESPAÇO (não removida) antes da
     * normalização: sem isso, `SIND.CAT.PROFISS.EMPREG.TRAB.V` viraria
     * `SINDCATPROFISSEMPREGRABV...` como um único token, e o `split(' ')[0]`
     * nunca casaria com `SIND`. Com a substituição, o primeiro token é `SIND`.
     *
     * @param {string} nome
     * @returns {boolean}
     */
    nomeIndicaPessoaJuridicaMasculina(nome = '') {
        const comSeparadores = String(nome).replaceAll(REGEX_SEPARADORES_PARA_ESPACO, ' ');
        const normalizado = normalizarTexto(comSeparadores);

        if (!normalizado) return false;

        const primeira = normalizado.split(' ', 1)[0];
        return TERMOS_PESSOA_JURIDICA_MASCULINA.has(primeira);
    }

    ePessoaJuridica(parte = {}) {
        const tipo = String(parte.tipoPessoa ?? '')
            .trim()
            .toUpperCase();

        if (['JURIDICA', 'JURÍDICA', 'PJ', 'J'].includes(tipo)) {
            return true;
        }

        if (['FISICA', 'FÍSICA', 'PF', 'F'].includes(tipo)) {
            return false;
        }

        // tipoPessoa ausente ou não conhecido — cai para a marca textual
        return this.nomeTemMarcaDePessoaJuridica(parte.nome ?? '');
    }

    /**
     * Localiza o processo correspondente na sessão.
     *
     * @param {string} numeroProcesso
     * @returns {object | undefined}
     */
    getProcessoPorNumero(numeroProcesso) {
        return this.sessaoStore.sessaoSelecionada.processos.find(
            (registro) =>
                NumeroProcessoUtil.formatarNumeroProcesso(registro.processo.numeroCompleto, false, true) ===
                NumeroProcessoUtil.formatarNumeroProcesso(numeroProcesso, false, true)
        );
    }

    /**
     * Detecta o flag `segredoJustica` em MÚLTIPLOS níveis do payload.
     *
     * Diferentes formatos de resposta colocam o flag em lugares distintos:
     *   · no próprio registro de análise (`registro.segredoJustica`);
     *   · no objeto do processo (`registro.processo.segredoJustica`);
     *   · aninhado em `registro.processo.processo.segredoJustica`;
     *   · no processo vindo da sessão (`sessaoStore...processos[i].processo.segredoJustica`).
     *
     * A primeira ocorrência `true` vence. É a fonte AUTORITATIVA — sobrepõe
     * o que vier do `mapaProcessosPartes`, que em formatos legados é apenas
     * um array de itens sem o flag.
     *
     * @param {object} registro
     * @param {object} [processoDaSessao]
     * @returns {boolean}
     */
    detectarSegredoJustica(registro, processoDaSessao) {
        return (
            registro?.segredoJustica === true ||
            registro?.processo?.segredoJustica === true ||
            registro?.processo?.processo?.segredoJustica === true ||
            processoDaSessao?.processo?.segredoJustica === true ||
            processoDaSessao?.segredoJustica === true
        );
    }

    /**
     * Localiza a entrada de partes do processo no `mapaProcessosPartes` e
     * devolve a lista de itens JUNTO com o flag `segredoJustica`.
     *
     * O flag vem preferencialmente do REGISTRO (fonte autoritativa) — o
     * mapa pode estar em formato legado (array) sem carregar o flag. Se a
     * entrada do mapa trouxer `segredoJustica: true`, ele também vence.
     *
     * @param {object} registro
     * @returns {{ itens: any[], segredoJustica: boolean }}
     */
    buscarPartesProcesso(registro) {
        const mapa = this.salaSessaoStore?.mapaProcessosPartes;
        const processo = this.getProcessoPorNumero(registro.numeroProcesso);
        const segredoDoRegistro = this.detectarSegredoJustica(registro, processo);

        if (!mapa) return { itens: [], segredoJustica: segredoDoRegistro };

        const chavesCandidatas = [
            processo?.processo?.numeroCompleto,
            registro.processo?.numeroCompleto,
            registro.numeroProcesso
        ].filter(Boolean);

        for (const chave of chavesCandidatas) {
            if (Object.prototype.hasOwnProperty.call(mapa, chave)) {
                return this.extrairEntradaDePartes(mapa[chave], segredoDoRegistro);
            }
        }

        const numeroNormalizado = NumeroProcessoUtil.formatarNumeroProcesso(registro.numeroProcesso, false, true);

        for (const [chaveMapa, entrada] of Object.entries(mapa)) {
            const chaveNormalizada = NumeroProcessoUtil.formatarNumeroProcesso(chaveMapa, false, true);

            if (chaveNormalizada === numeroNormalizado) {
                return this.extrairEntradaDePartes(entrada, segredoDoRegistro);
            }
        }

        return { itens: [], segredoJustica: segredoDoRegistro };
    }

    /**
     * A partir da entrada bruta do `mapaProcessosPartes`, extrai a lista
     * de itens e o flag `segredoJustica`.
     *
     * O flag tem duas fontes, na ordem:
     *   1) `entrada.segredoJustica === true` — quando o mapa já carrega o
     *      objeto de processo com o campo no topo;
     *   2) `segredoFallback` — o flag detectado no registro de análise,
     *      usado quando o mapa é apenas um array de itens (formato legado)
     *      sem o campo.
     *
     * @param {any} entrada
     * @param {boolean} [segredoFallback]
     * @returns {{ itens: any[], segredoJustica: boolean }}
     */
    extrairEntradaDePartes(entrada, segredoFallback = false) {
        return {
            itens: this.extrairListaDePartes(entrada),
            segredoJustica: entrada?.segredoJustica === true || segredoFallback
        };
    }

    extrairListaDePartes(entrada) {
        if (Array.isArray(entrada)) return entrada;
        if (Array.isArray(entrada?.partes)) return entrada.partes;
        if (Array.isArray(entrada?.partesProcessuais)) return entrada.partesProcessuais;
        return [];
    }

    /**
     * Nome a ser usado em AGRUPAMENTO, ORDENAÇÃO, TEXTO PROVÁVEL e
     * COMPARAÇÃO. Quando o processo está sob segredo de justiça, aplica
     * `NomeUtil.abreviarNome`; senão, devolve o nome original.
     *
     * NÃO usar para exibição do bloco "Dados do cadastro" — esse bloco
     * precisa mostrar o nome ORIGINAL (`parte.nome`), como veio do JSON,
     * para fins de auditoria.
     *
     * @param {ParteProcessual} parte
     * @returns {string}
     */
    getNomeParaComparacao(parte = {}) {
        const nome = parte.nome ?? '';

        if (!nome) return '';
        if (!parte.segredoJustica) return nome;
        return NomeUtil.abreviarNome(nome);
    }

    extrairPartesDoCadastro(registro) {
        const { itens, segredoJustica } = this.buscarPartesProcesso(registro);
        const partes = [];
        let ordem = 0;

        for (const item of itens) {
            const denominacao = item.denominacao ?? '';
            const tipoPolo = item.tipoPolo ?? '';

            if (Array.isArray(item.partes)) {
                for (const sub of item.partes) {
                    partes.push({
                        denominacaoOriginal: denominacao,
                        denominacoes: this.dividirDenominacoesCompostas(denominacao),
                        nome: sub.parte?.nome ?? '',
                        segredoJustica,
                        tipoPolo,
                        ordem: ordem++,
                        cpfCnpj: sub.parte?.cpfCnpj,
                        tipoPessoa: sub.parte?.tipoPessoa,
                        codigo: sub.parte?.codigo
                    });
                }
                continue;
            }

            partes.push({
                denominacaoOriginal: denominacao,
                denominacoes: this.dividirDenominacoesCompostas(denominacao),
                nome: item.nome ?? '',
                segredoJustica,
                tipoPolo,
                ordem: ordem++,
                cpfCnpj: item.cpfCnpj,
                tipoPessoa: item.tipoPessoa,
                codigo: item.codigo
            });
        }

        return partes;
    }

    /**
     * Decompõe uma denominação do CADASTRO em lista de denominações simples.
     * Aceita vírgulas e ` e ` como separadores.
     *
     * Ex.: `Agravante(s), Agravado(a)(s), Recorrente(s) e Recorrido(a)(s)`
     *       → `['Agravante', 'Agravado', 'Recorrente', 'Recorrido']`.
     *
     * @param {string} denominacao
     * @returns {string[]}
     */
    dividirDenominacoesCompostas(denominacao = '') {
        return String(denominacao)
            .split(REGEX_SEPARADOR_DENOMINACOES)
            .map((parte) => this.removerMarcadorPlural(parte.trim()))
            .filter(Boolean);
    }

    /**
     * Limpa um token de denominação: remove pontuação final, marcadores
     * pluralizadores em qualquer posição e pontuação residual.
     *
     * @param {string} token
     * @returns {string}
     */
    limparTokenDeDenominacao(token = '') {
        return String(token)
            .replace(REGEX_PONTUACAO_FINAL, '')
            .replaceAll(REGEX_MARCADOR_PLURAL, '')
            .replace(REGEX_PONTUACAO_FINAL, '')
            .replaceAll(/\s+/g, ' ')
            .trim();
    }

    /**
     * Tenta reconhecer a MAIOR denominação conhecida a partir de
     * `tokens[inicio]`. Testa 4, 3, 2 e 1 palavras, e para cada tamanho
     * checa as variações normalizadas contra o dicionário.
     *
     * @param {string[]} tokens
     * @param {number} inicio
     * @returns {{ tamanho: number } | null}
     */
    matchDenominacaoEm(tokens, inicio) {
        if (inicio >= tokens.length) return null;

        const maxTamanho = Math.min(MAX_PALAVRAS_DENOMINACAO, tokens.length - inicio);

        for (let tamanho = maxTamanho; tamanho >= 1; tamanho--) {
            const fragmento = tokens.slice(inicio, inicio + tamanho);
            const frase = fragmento.join(' ');

            for (const variante of gerarVariantesDeFrase(frase)) {
                if (DENOMINACOES_NORMALIZADAS.has(variante)) {
                    return { tamanho };
                }
            }
        }

        return null;
    }

    /**
     * Caminha pelos tokens a partir do início do trecho (após o verbo) e
     * consome denominações conhecidas. Conectores `e`/`E` só são consumidos
     * quando o próximo token também é uma denominação conhecida.
     *
     * @param {string[]} tokens
     * @returns {{ denominacoes: { texto: string, tokens: string[] }[], proximoIndice: number }}
     */
    identificarDenominacoesNoTrecho(tokens) {
        const denominacoes = [];
        let index = 0;

        while (index < tokens.length) {
            const token = tokens[index];

            if (/^[eE]$/.test(token)) {
                if (this.matchDenominacaoEm(tokens, index + 1)) {
                    index++;
                    continue;
                }
                break;
            }

            const encontrado = this.matchDenominacaoEm(tokens, index);

            if (!encontrado) break;

            const fragmento = tokens.slice(index, index + encontrado.tamanho);
            const texto = fragmento.join(' ').replace(REGEX_PONTUACAO_FINAL, '').trim();

            denominacoes.push({ texto, tokens: fragmento });
            index += encontrado.tamanho;
        }

        return { denominacoes, proximoIndice: index };
    }

    /**
     * Converte o texto do voto em lista de {@link ParteProcessual}.
     *
     * @param {string} texto
     * @returns {ParteProcessual[]}
     */
    parsePartesDoVoto(texto = '') {
        if (!texto) return [];

        const trechos = texto.split(REGEX_SEPARADOR_TRECHOS);
        const partes = [];

        for (const trecho of trechos) {
            const partesDoTrecho = this.parseTrechoDoVoto(trecho);
            partes.push(...partesDoTrecho);
        }

        return partes;
    }

    /**
     * Processa um trecho do voto, caminhando token a token a partir do verbo.
     *
     * @param {string} trecho
     * @returns {ParteProcessual[]}
     */
    parseTrechoDoVoto(trecho = '') {
        const match = trecho.trim().match(REGEX_INICIO_TRECHO_SIMPLES);

        if (!match) return [];

        const verbo = match[1].toLowerCase();
        const resto = match[2].trim();

        if (!resto) return [];

        const tokens = resto.split(/\s+/).filter(Boolean);
        const { denominacoes, proximoIndice } = this.identificarDenominacoesNoTrecho(tokens);

        if (denominacoes.length === 0) return [];

        const restoTokens = tokens.slice(proximoIndice);
        let inicio = 0;

        while (inicio < restoTokens.length && REGEX_TOKEN_CONECTOR.test(restoTokens[inicio])) {
            inicio++;
        }

        const nomeTexto = restoTokens.slice(inicio).join(' ').trim();

        if (!nomeTexto) return [];

        const nomes =
            verbo === 'são'
                ? nomeTexto
                      .split(REGEX_SEPARADOR_NOMES_PLURAL)
                      .map((n) => n.trim())
                      .filter(Boolean)
                : [nomeTexto];

        const denominacoesTexto = denominacoes.map((d) => d.texto);

        return Array.from(nomes, (nome) => ({ denominacoes: denominacoesTexto, nome }));
    }

    /**
     * Normaliza um nome para comparação: uppercase, espaços colapsados.
     *
     * @param {string} nome
     * @returns {string}
     */
    normalizarNome(nome = '') {
        return String(nome).toUpperCase().replaceAll(/\s+/g, ' ').trim();
    }

    /**
     * Extrai o prenome (primeiro token) de um nome completo.
     *
     * @param {string} nome
     * @returns {string}
     */
    extrairPrenome(nome = '') {
        const normalizado = String(nome).trim().toUpperCase();

        if (!normalizado) return '';
        return normalizado.split(REGEX_PARTES_DO_NOME)[0] ?? '';
    }

    /**
     * Detecta o gênero presumido de uma parte processual.
     *
     * Ordem de precedência:
     *   1) nome começa com termo da lista TERMOS_PESSOA_JURIDICA_MASCULINA
     *      (ex.: `BANCO`, `GRUPO`) → masculino, porque o substantivo
     *      implícito é masculino ("o banco Agravado", "o grupo Agravado");
     *   2) pessoa jurídica (por `tipoPessoa` ou por marca textual) →
     *      feminino;
     *   3) prenome termina em TERMINACOES_FEMININAS → feminino;
     *   4) demais → masculino.
     *
     * @param {ParteProcessual} parte
     * @returns {'feminino'|'masculino'}
     */
    detectarGeneroDaParte(parte = {}) {
        // Usa o nome ORIGINAL — o prenome não muda com a abreviação
        // (NomeUtil tipicamente reduz prenomes a inicial ou mantém).
        if (this.nomeIndicaPessoaJuridicaMasculina(parte.nome ?? '')) {
            return 'masculino';
        }

        if (this.ePessoaJuridica(parte)) return 'feminino';

        const prenome = this.extrairPrenome(parte.nome ?? '');

        if (!prenome) return 'masculino';

        if (TERMINACOES_FEMININAS.some((sufixo) => prenome.endsWith(sufixo))) {
            return 'feminino';
        }

        return 'masculino';
    }

    /**
     * Junta uma lista de strings na forma de lista portuguesa:
     *
     *   ['A']                  → 'A'
     *   ['A', 'B']             → 'A e B'
     *   ['A', 'B', 'C']        → 'A, B e C'
     *   ['A', 'B', 'C', 'D']   → 'A, B, C e D'
     *
     * @param {string[]} itens
     * @param {string} conjuncao
     * @returns {string}
     */
    juntarComConjuncao(itens = [], conjuncao = 'e') {
        const naoVazios = (itens || []).filter(Boolean);

        if (naoVazios.length === 0) return '';
        if (naoVazios.length === 1) return naoVazios[0];
        if (naoVazios.length === 2) return `${naoVazios[0]} ${conjuncao} ${naoVazios[1]}`;

        const ultimo = naoVazios.at(-1);
        const anteriores = naoVazios.slice(0, -1).join(', ');
        return `${anteriores} ${conjuncao} ${ultimo}`;
    }

    /**
     * Reordena uma lista de nomes segundo uma função `getPosicao`, que
     * devolve a posição de cada nome no voto (ou `Infinity` quando não
     * encontrado). Empates preservam a ordem original do cadastro.
     *
     * @param {string[]} nomes
     * @param {(nome: string) => number} getPosicao
     * @returns {string[]}
     */
    ordenarNomesPelaReferencia(nomes = [], getPosicao) {
        if (nomes.length < 2 || typeof getPosicao !== 'function') return [...nomes];

        const indexados = nomes.map((nome, index) => ({
            nome,
            idx: index,
            posicao: getPosicao(nome)
        }));

        return indexados
            .toSorted((a, b) => {
                if (a.posicao !== b.posicao) return a.posicao - b.posicao;
                return a.idx - b.idx;
            })
            .map((item) => item.nome);
    }

    /**
     * Agrupa as partes do cadastro em baldes por CONJUNTO de denominações
     * (eventualmente acrescido do polo, no modo `'polo'`).
     *
     * NUNCA mistura denominações diferentes no mesmo balde — cada balde
     * tem um conjunto ÚNICO de denominações, o que garante que o texto
     * reconstruído seja bem-formado.
     *
     * ORDEM DAS DENOMINAÇÕES: preserva a ordem em que aparecem no cadastro
     * (primeiro membro do grupo). Ordenação alfabética é usada APENAS para
     * a chave do grupo — assim partes com o mesmo conjunto em ordens
     * diferentes caem no mesmo balde, mas a exibição segue o cadastro,
     * que costuma refletir a ordem que o autor usou no voto.
     *
     * SEGREDO DE JUSTIÇA: o flag é copiado para o grupo. Quando true, as
     * denominações são flexionadas apenas em NÚMERO (plural); a flexão
     * de GÊNERO é bloqueada. Ver JSDoc de `formatarDenominacao`.
     *
     * @param {ParteProcessual[]} partes
     * @param {'denominacao'|'polo'} modo
     * @returns {Map<string, GrupoDePartes>}
     */
    agruparPartesParaTexto(partes, modo) {
        const grupos = new Map();
        const partesOrdenadas = [...partes].toSorted((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

        for (const parte of partesOrdenadas) {
            const nome = this.getNomeParaComparacao(parte).trim();

            if (!nome) continue;

            const denomsOriginais = (parte.denominacoes || []).filter(Boolean);
            const denomsChave = [...denomsOriginais].toSorted((a, b) => a.localeCompare(b));
            const chaveDenom = denomsChave.join('|');
            const chave = modo === 'polo' ? `POLO:${parte.tipoPolo ?? ''}|DENOM:${chaveDenom}` : `DENOM:${chaveDenom}`;

            if (!grupos.has(chave)) {
                grupos.set(chave, {
                    denominacoes: denomsOriginais,
                    nomes: [],
                    generos: [],
                    segredoJustica: parte.segredoJustica === true
                });
            }

            const grupo = grupos.get(chave);
            grupo.nomes.push(nome);
            grupo.generos.push(this.detectarGeneroDaParte(parte));
        }

        return grupos;
    }

    /**
     * Agrupa as partes do cadastro por NOME, reunindo TODAS as denominações
     * de cada parte. Modo `'parte'` — necessário quando a mesma parte é
     * citada em grupos diferentes (ex.: AGRAVANTE e RECORRENTE).
     *
     * Cada grupo tem exatamente 1 nome, e a lista de denominações segue a
     * ordem de primeira aparição no cadastro.
     *
     * SEGREDO DE JUSTIÇA: o flag é copiado para o grupo. Quando true, as
     * denominações são flexionadas apenas em NÚMERO (plural); a flexão
     * de GÊNERO é bloqueada. Ver JSDoc de `formatarDenominacao`.
     *
     * @param {ParteProcessual[]} partes
     * @returns {Map<string, GrupoDePartes>}
     */
    agruparPartesPorNome(partes) {
        const grupos = new Map();
        const partesOrdenadas = [...partes].toSorted((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

        for (const parte of partesOrdenadas) {
            const nome = this.getNomeParaComparacao(parte).trim();

            if (!nome) continue;

            const chave = chaveDeComparacaoDeNome(nome);

            if (!chave) continue;

            if (!grupos.has(chave)) {
                grupos.set(chave, {
                    denominacoes: [],
                    denominacoesSet: new Set(),
                    nomes: [nome],
                    generos: [this.detectarGeneroDaParte(parte)],
                    segredoJustica: parte.segredoJustica === true
                });
            }

            const grupo = grupos.get(chave);

            for (const denom of parte.denominacoes || []) {
                const chaveDenom = normalizarDenominacaoParaChave(denom);

                if (!grupo.denominacoesSet.has(chaveDenom)) {
                    grupo.denominacoesSet.add(chaveDenom);
                    grupo.denominacoes.push(denom);
                }
            }
        }

        // Remove o Set auxiliar antes de devolver — não faz parte do
        // contrato de GrupoDePartes.
        for (const grupo of grupos.values()) {
            delete grupo.denominacoesSet;
        }

        return grupos;
    }

    /**
     * Constrói os mapas de posição usados para reordenar nomes segundo o
     * voto. Duas camadas:
     *
     *   1) `porAssinaturaENome` — chave `${assinatura das denominações}|${nome}`.
     *      Casa com precisão a posição da parte no SEGMENTO correspondente
     *      do voto. Necessário quando um mesmo nome aparece em mais de um
     *      segmento (ex.: `AGRAVANTES FULANO` e `AGRAVADOS FULANO`).
     *   2) `porNome` — chave apenas o nome. Fallback global para grupos
     *      que não têm correspondente direto no voto (ex.: modo `parte`).
     *
     * NOTA: o mapa é construído a partir das partes DO VOTO (`partesDoVoto`),
     * que já vêm anonimizadas pelo próprio texto do voto (presume-se).
     * Nada a abreviar aqui.
     *
     * @param {ParteProcessual[]} partesDoVoto
     * @returns {{ porAssinaturaENome: Map<string, number>, porNome: Map<string, number> }}
     */
    construirMapasDePosicao(partesDoVoto = []) {
        const porAssinaturaENome = new Map();
        const porNome = new Map();

        (partesDoVoto || []).forEach((parte, index) => {
            const nomeKey = chaveDeComparacaoDeNome(parte.nome);

            if (!nomeKey) return;

            if (!porNome.has(nomeKey)) {
                porNome.set(nomeKey, index);
            }

            const sig = assinaturaDenominacoes(parte.denominacoes);
            const chave = `${sig}|${nomeKey}`;

            if (!porAssinaturaENome.has(chave)) {
                porAssinaturaENome.set(chave, index);
            }
        });

        return { porAssinaturaENome, porNome };
    }

    /**
     * Monta um grupo de partes no formato `"verbo + denominações + nomes"`,
     * com a chave de ordenação do grupo (menor posição entre seus nomes no
     * voto).
     *
     * A posição de cada nome é resolvida em duas camadas — ver JSDoc de
     * `construirMapasDePosicao`. O nome já vem de `getNomeParaComparacao`
     * (abreviado quando segredo de justiça), então o casamento com o voto
     * usa a mesma forma.
     *
     * Quando `grupo.segredoJustica === true`, as denominações são
     * flexionadas apenas em NÚMERO; a flexão de GÊNERO é bloqueada — ver
     * JSDoc de `formatarDenominacao`.
     *
     * @param {GrupoDePartes} grupo
     * @param {'titulo'|'maiuscula'} caixaDenominacao
     * @param {{ porAssinaturaENome: Map<string, number>, porNome: Map<string, number> }} mapas
     * @returns {{ texto: string, posicao: number }}
     */
    montarGrupoDePartes(grupo, caixaDenominacao, mapas) {
        const grupoSig = assinaturaDenominacoes(grupo.denominacoes);
        const isSegredoJustica = grupo.segredoJustica === true;

        const getPosicao = (nome) => {
            const nomeKey = chaveDeComparacaoDeNome(nome);
            const chaveSig = `${grupoSig}|${nomeKey}`;

            if (mapas.porAssinaturaENome.has(chaveSig)) return mapas.porAssinaturaENome.get(chaveSig);
            if (mapas.porNome.has(nomeKey)) return mapas.porNome.get(nomeKey);
            return Infinity;
        };

        const nomesOrdenados = this.ordenarNomesPelaReferencia(grupo.nomes, getPosicao);

        // Mantém o gênero associado ao nome original (índice no array do
        // cadastro), mesmo depois da reordenação.
        const generosOrdenados = nomesOrdenados.map((nome, index) => {
            const indexOriginal = grupo.nomes.indexOf(nome);
            return grupo.generos[indexOriginal === -1 ? index : indexOriginal];
        });

        const isPlural = nomesOrdenados.length > 1;
        const verbo = isPlural ? 'são' : 'é';
        const genero = generosOrdenados.every((g) => g === 'feminino') ? 'feminino' : 'masculino';

        const denominacoesFormatadas = grupo.denominacoes.map((denominacao) =>
            this.formatarDenominacao({
                denominacao,
                plural: isPlural,
                genero,
                caixaDenominacao,
                segredoJustica: isSegredoJustica
            })
        );

        const denominacoesTexto = this.juntarComConjuncao(denominacoesFormatadas);
        const nomeTexto = this.juntarComConjuncao(nomesOrdenados);

        const posicoes = nomesOrdenados.map(getPosicao);
        const posicao = posicoes.length > 0 ? Math.min(...posicoes) : Infinity;

        return {
            texto: `${verbo} ${denominacoesTexto} ${nomeTexto}`,
            posicao
        };
    }

    /**
     * Reconstrói um texto de partes a partir do cadastro.
     *
     * Três modos:
     *   · `'denominacao'` (padrão): agrupa por conjunto de denominações,
     *     ignorando o polo.
     *   · `'polo'`: agrupa por (POLO + conjunto de denominações).
     *   · `'parte'`: agrupa por NOME da parte, reunindo as denominações
     *     daquela parte com `e` — útil quando a mesma parte tem papéis
     *     diferentes (AGRAVANTE e RECORRENTE).
     *
     * ORDEM DO TEXTO PROVÁVEL = ORDEM DO VOTO:
     *   Dentro de cada grupo, os nomes são reordenados pela posição que
     *   ocupam no voto — resolvida por `(assinatura das denominações +
     *   nome)` e, como fallback, apenas por nome. Nomes ausentes no voto
     *   ficam no fim. Os grupos são emitidos na ordem de primeira aparição
     *   dos seus nomes no voto.
     *
     * ORDEM DAS DENOMINAÇÕES DENTRO DO GRUPO = ORDEM DO CADASTRO:
     *   As denominações de cada grupo seguem a ordem em que aparecem no
     *   cadastro (do primeiro membro do grupo) — o autor costuma seguir
     *   essa mesma ordem no voto, e a ordenação alfabética geraria
     *   divergência artificial de ordem.
     *
     * Nomes: os nomes usados aqui são os de COMPARAÇÃO
     * (`getNomeParaComparacao`), ou seja, abreviados quando o processo
     * está sob segredo de justiça.
     *
     * Denominações: sob segredo de justiça, o NÚMERO (plural) é aplicado
     * normalmente; a flexão de GÊNERO é bloqueada — ver
     * `formatarDenominacao`.
     *
     * @param {ParteProcessual[]} partes
     * @param {string} textoVoto
     * @param {'denominacao'|'polo'|'parte'} [modo]
     * @param {ParteProcessual[]} [partesDoVoto]  referência para ordenação
     * @returns {string}
     */
    montarTextoPartesDoCadastro(partes, textoVoto = '', modo = 'denominacao', partesDoVoto = []) {
        if (!partes?.length) return '';

        const caixaDenominacao = this.detectarCaixaDenominacaoVoto(textoVoto);

        const grupos = modo === 'parte' ? this.agruparPartesPorNome(partes) : this.agruparPartesParaTexto(partes, modo);

        const mapas = this.construirMapasDePosicao(partesDoVoto);

        const gruposMontados = [...grupos.values()].map((grupo) =>
            this.montarGrupoDePartes(grupo, caixaDenominacao, mapas)
        );

        const trechos = gruposMontados.toSorted((a, b) => a.posicao - b.posicao).map((item) => item.texto);

        return this.juntarComConjuncao(trechos);
    }

    normalizarDenominacaoParaDiff(denominacao = '') {
        let texto = String(denominacao).toUpperCase();

        if (!texto) return '';

        if (REGEX_TERMINACAO_PLURAL_CLARA.test(texto) && texto.length > 2) {
            texto = texto.slice(0, -1);
        }

        if (texto.endsWith('A') && texto.length > 1) {
            texto = texto.slice(0, -1) + 'O';
        }

        return texto;
    }

    /**
     * Reconstrói um texto em linguagem natural a partir de uma lista de
     * partes, com flexão normalizada (singular, masculino). Usado apenas
     * para o cálculo do sub-filtro "incluir divergências de morfologia
     * flexional" — quando DESLIGADO, precisamos saber se o diff normalizado
     * é vazio, e isso exige essa versão normalizada. Não altera o diff
     * exibido.
     *
     * A emissão mantém a estrutura `verbo denominação(ões) nome(s)`, agrupando
     * por conjunto de denominações normalizadas. A ordem dos grupos é
     * normalizada por primeiro nome, e os nomes dentro de cada grupo também
     * são ordenados.
     *
     * @param {ParteProcessual[]} partes
     * @returns {string}
     */
    formatarPartesNormalizado(partes = []) {
        if (!partes?.length) return '';

        const partesOrdenadas = [...partes].toSorted((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        const grupos = new Map();

        for (const parte of partesOrdenadas) {
            // Usa o nome de COMPARAÇÃO para o filtro — coerente com o texto
            // provável. Sem isso, o filtro "incluir divergências flexionais"
            // acusaria diferenças entre nome completo (cadastro) e abreviado
            // (voto) que são, na verdade, efeito do segredo de justiça.
            const nome = this.normalizarNome(this.getNomeParaComparacao(parte));

            if (!nome) continue;

            const denoms = (parte.denominacoes || [])
                .map((d) => this.normalizarDenominacaoParaDiff(this.removerMarcadorPlural(d)))
                .filter(Boolean)
                .toSorted((a, b) => a.localeCompare(b));

            const chave = denoms.join('|');

            if (!grupos.has(chave)) {
                grupos.set(chave, { denominacoes: denoms, nomes: [] });
            }

            grupos.get(chave).nomes.push(nome);
        }

        const trechos = [];

        for (const { denominacoes, nomes } of grupos.values()) {
            const isPlural = nomes.length > 1;
            const verbo = isPlural ? 'são' : 'é';
            const denominacoesTexto = this.juntarComConjuncao(denominacoes);
            const nomesTexto = this.juntarComConjuncao(nomes);

            trechos.push(`${verbo} ${denominacoesTexto} ${nomesTexto}`);
        }

        return this.juntarComConjuncao(trechos);
    }

    /**
     * Formata uma denominação para o texto provável.
     *
     * Aplica:
     *   · MARCador pluralizador — sempre removido do valor de entrada;
     *   · NÚMERO (plural) — quando `plural === true`, aplica pluralização;
     *   · GÊNERO — quando `genero === 'feminino'`, feminiliza a terminação
     *     `O` → `A`.
     *
     * Sob SEGREDO DE JUSTIÇA (`segredoJustica === true`), o NÚMERO é
     * aplicado normalmente — a contagem de nomes no grupo é um dado
     * OBSERVÁVEL do cadastro, não uma inferência. O GÊNERO, porém, NÃO é
     * aplicado: os nomes vêm abreviados (siglas/iniciais) e o gênero real
     * da parte não pode ser inferido — feminilizar seria uma invenção.
     *
     * Exemplo sob segredo:
     *   · `são Agravantes E.B.C.T.E. e U.F.M.G.`  — plural aplicado;
     *   · `são Agravadas E.B.C.T.E. e U.F.M.G.`   — feminino NÃO aplicado.
     *
     * @param {object} params
     * @param {string} params.denominacao
     * @param {boolean} params.plural
     * @param {'masculino'|'feminino'} params.genero
     * @param {'titulo'|'maiuscula'} params.caixaDenominacao
     * @param {boolean} [params.segredoJustica]
     * @returns {string}
     */
    formatarDenominacao({ denominacao, plural, genero, caixaDenominacao, segredoJustica = false }) {
        const base = this.removerMarcadorPlural(denominacao);

        // Sob segredo, pulamos a flexão de gênero mas mantemos a de número.
        const singular = segredoJustica ? base : this.aplicarGeneroSingular(base, genero);

        if (!plural) {
            return this.aplicarCaixa(singular, caixaDenominacao);
        }

        return this.pluralizarDenominacao(singular, caixaDenominacao);
    }

    /**
     * Remove marcador pluralizador em qualquer posição da string.
     *
     * @param {string} denominacao
     * @returns {string}
     */
    removerMarcadorPlural(denominacao = '') {
        return denominacao.replaceAll(REGEX_MARCADOR_PLURAL, '').replaceAll(/\s+/g, ' ').trim();
    }

    /**
     * Flexiona a denominação para o feminino, no singular.
     *
     * @param {string} denominacao
     * @param {'feminino'|'masculino'} genero
     * @returns {string}
     */
    aplicarGeneroSingular(denominacao, genero) {
        if (genero !== 'feminino') return denominacao;

        const upper = denominacao.toUpperCase();

        if (upper.endsWith('O') && denominacao.length > 1) {
            return denominacao.slice(0, -1) + 'a';
        }

        return denominacao;
    }

    /**
     * Aplica a caixa escolhida sobre o texto.
     *
     * @param {string} texto
     * @param {'maiuscula'|'titulo'} caixa
     * @returns {string}
     */
    aplicarCaixa(texto, caixa) {
        if (caixa === 'maiuscula') return texto.toUpperCase();
        return this.paraTitulo(texto);
    }

    /**
     * Title Case simples: primeira letra maiúscula, restante minúsculo.
     *
     * @param {string} texto
     * @returns {string}
     */
    paraTitulo(texto = '') {
        if (!texto) return texto;
        return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
    }

    /**
     * Pluraliza denominações processuais comuns em português.
     *
     * @param {string} denominacao
     * @param {'maiuscula'|'titulo'} caixa
     * @returns {string}
     */
    pluralizarDenominacao(denominacao, caixa) {
        const upper = denominacao.toUpperCase();

        if (!upper) return upper;

        const ultimaLetra = upper.slice(-1);
        let plural;

        switch (ultimaLetra) {
            case 'S': {
                plural = upper;
                break;
            }
            case 'R':
            case 'Z': {
                plural = upper + 'ES';
                break;
            }
            case 'M': {
                plural = upper.slice(0, -1) + 'NS';
                break;
            }
            case 'L': {
                plural = upper.slice(0, -1) + 'IS';
                break;
            }
            default: {
                plural = upper + 'S';
            }
        }

        return this.aplicarCaixa(plural, caixa);
    }

    /**
     * Detecta o estilo de caixa das denominações no voto (maiúsculas vs.
     * título), olhando a primeira ocorrência de "é/são DENOMINAÇÃO".
     *
     * @param {string} textoVoto
     * @returns {'maiuscula'|'titulo'}
     */
    detectarCaixaDenominacaoVoto(textoVoto = '') {
        const match = textoVoto.match(REGEX_PRIMEIRA_DENOMINACAO);

        if (!match) return 'titulo';

        const palavra = match[1];
        const letras = palavra.replaceAll(/[^\p{L}]/gu, '');

        if (letras.length > 0 && letras === letras.toUpperCase()) return 'maiuscula';
        return 'titulo';
    }

    /**
     * Renderiza o bloco "Dados do cadastro" — nome ORIGINAL, como veio do
     * JSON. NÃO aplica abreviação por segredo de justiça: essa coluna é
     * de auditoria e deve mostrar o dado cru.
     *
     * @param {ParteProcessual[]} partes
     * @returns {string}
     */
    formatarPartesBrutas(partes = []) {
        if (!partes?.length) return '';

        return partes
            .map((parte) => {
                const denominacao = (parte.denominacaoOriginal ?? '').trim();
                let sufixoTipo = '';

                if (parte.tipoPessoa) {
                    sufixoTipo = ` (${parte.tipoPessoa})`;
                } else if (this.nomeTemMarcaDePessoaJuridica(parte.nome ?? '')) {
                    sufixoTipo = ' (JURIDICA)';
                }

                return `• ${denominacao} — ${parte.nome}${sufixoTipo}`;
            })
            .join('\n');
    }

    renderConteudoFontePartes(partesDoCadastro, variantes) {
        const textoBruto = this.formatarPartesBrutas(partesDoCadastro);
        const variantesValidas = (variantes || []).filter((v) => v.texto);

        if (!textoBruto && variantesValidas.length === 0) {
            return <Typography sx={{ fontSize: 14, fontStyle: 'italic', color: grey[600] }}>{TEXTO_VAZIO}</Typography>;
        }

        return (
            <Stack spacing={1}>
                {textoBruto ? (
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: grey[500],
                                fontStyle: 'italic',
                                letterSpacing: 0.3,
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 0.25
                            }}
                        >
                            Dados do cadastro
                        </Typography>
                        <Typography sx={TEXT_STYLES_BRUTO}>{textoBruto}</Typography>
                    </Box>
                ) : null}

                {variantesValidas.map((variante, index) => (
                    <Box key={`${variante.modo}-${index}`}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: grey[500],
                                fontStyle: 'italic',
                                letterSpacing: 0.3,
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 0.25
                            }}
                        >
                            {`${variante.principal ? 'Texto provável' : 'Texto provável alternativo'} — agrupamento por ${ROTULOS_MODO_AGRUPAMENTO[variante.modo]}`}
                        </Typography>
                        <Typography
                            sx={
                                variante.principal
                                    ? TEXT_STYLES_MULTILINHA
                                    : { ...TEXT_STYLES_MULTILINHA, color: grey[600] }
                            }
                        >
                            {variante.texto}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        );
    }

    renderTextoSolto(texto) {
        if (!texto) {
            return <Typography sx={{ fontSize: 14, fontStyle: 'italic', color: grey[600] }}>{TEXTO_VAZIO}</Typography>;
        }

        return <Typography sx={TEXT_STYLES_MULTILINHA}>{texto}</Typography>;
    }

    get titulo() {
        let titulo =
            'Relatório para conferência das informações disponíveis no voto/acórdão em relação às informações de cadastro e da decisão da certidão';

        if (this.sessaoStore.sessaoSelecionada) {
            const { tipo, orgaoJudicante } = this.sessaoStore.sessaoSelecionada;
            titulo += '\n';
            titulo += tipo === 'O' ? 'Sessão Ordinária' : 'Sessão Extraordinária';

            titulo += ' - ';
            titulo += orgaoJudicante.descricao;

            titulo += ' - ';
            titulo += moment(
                this.sessaoStore.sessaoSelecionada.prazos.dataInicioSessaoPresencial,
                'DD-MM-YYYY-HH-mm-ss'
            ).format('DD/MM/YYYY HH:mm');

            titulo += ' - ';
            titulo += this.sessaoStore.sessaoSelecionada.descricao;
        }

        return titulo;
    }

    isComparacaoHabilitada(tipo) {
        switch (tipo) {
            case 'classeNumero':
                return this.exibirComparacaoClasseNumero;
            case 'decisao':
                return this.exibirComparacaoDecisao;
            case 'partes':
                return this.exibirComparacaoPartes;
            default:
                return true;
        }
    }

    /**
     * Rótulo curto da origem do conteúdo da 2ª coluna.
     *
     * @param {'certidao'|'cadastro'} fonte
     * @returns {string}
     */
    getRotuloFonte(fonte) {
        return fonte === 'certidao' ? 'certidão' : 'cadastro';
    }

    /**
     * Renderiza um item de switch da lista de configurações.
     *
     * @param {object} params
     * @param {string} params.id
     * @param {string} params.label
     * @param {boolean} params.checked
     * @param {(event: object) => void} params.onChange
     * @param {React.ReactNode} [params.icon]
     * @param {object} [params.sx]  sobrescreve o `sx` do ListItem
     * @returns {React.ReactNode}
     */
    renderColunaSwitch({ id, label, checked, onChange, icon, sx = {} }) {
        return (
            <ListItem sx={sx}>
                <ListItemIcon>{icon ?? <SyncIcon />}</ListItemIcon>
                <ListItemText
                    id={id}
                    primary={label}
                />
                <Switch
                    checked={checked}
                    color="primary"
                    name={label}
                    onChange={onChange}
                    size="small"
                    slotProps={{ input: { 'aria-labelledby': id } }}
                />
            </ListItem>
        );
    }

    /**
     * Bloco de configurações no cabeçalho (fora da área de rolagem da tabela).
     * Duas colunas: "Comparações" (o que é exibido e filtrado) e "Exibição de
     * processos" (filtro por presença/ausência de divergências nas comparações
     * ativas). O sub-filtro "Incluir divergências de morfologia flexional" só
     * aparece quando "Exibir processos com divergências..." está ligado — é
     * um refinamento desse filtro.
     *
     * @returns {React.ReactNode}
     */
    renderConfiguracoes() {
        return (
            <Box sx={{ width: '100%', mt: 1 }}>
                <Button
                    endIcon={this.configuracoesAbertas ? <ExpandLess /> : <ExpandMore />}
                    onClick={this.handleToggleConfiguracoes}
                    size="small"
                    startIcon={<Settings />}
                    sx={{ textTransform: 'none' }}
                >
                    Configurações
                </Button>
                <Collapse
                    unmountOnExit
                    in={this.configuracoesAbertas}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 0, md: 2 },
                            mt: 1,
                            p: 1,
                            border: ESTILO_BORDA,
                            borderRadius: 1,
                            backgroundColor: grey[50]
                        }}
                    >
                        <List
                            dense
                            subheader={<ListSubheader disableSticky>Comparações</ListSubheader>}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            {this.renderColunaSwitch({
                                id: 'switch-list-label-exibir-comparacao-classe-numero',
                                label: 'Comparar classe e número',
                                checked: this.exibirComparacaoClasseNumero,
                                onChange: this.handleChangeExibirComparacaoClasseNumero,
                                icon: <Numbers />
                            })}
                            {this.renderColunaSwitch({
                                id: 'switch-list-label-exibir-comparacao-decisao',
                                label: 'Comparar decisão',
                                checked: this.exibirComparacaoDecisao,
                                onChange: this.handleChangeExibirComparacaoDecisao,
                                icon: <Gavel />
                            })}
                            {this.renderColunaSwitch({
                                id: 'switch-list-label-exibir-comparacao-partes',
                                label: 'Comparar partes',
                                checked: this.exibirComparacaoPartes,
                                onChange: this.handleChangeExibirComparacaoPartes,
                                icon: <Groups />
                            })}
                        </List>
                        <Divider
                            flexItem
                            orientation="vertical"
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        />
                        <List
                            dense
                            subheader={<ListSubheader disableSticky>Exibição de processos</ListSubheader>}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            {this.renderColunaSwitch({
                                id: 'switch-list-label-exibir-processos-com-divergencias',
                                label: 'Exibir processos com divergências para a configuração selecionada',
                                checked: this.exibirProcessosComDivergencias,
                                onChange: this.handleChangeExibirProcessosComDivergencias,
                                icon: <SyncDisabledIcon />
                            })}
                            {this.exibirProcessosComDivergencias
                                ? this.renderColunaSwitch({
                                      id: 'switch-list-label-incluir-divergencias-flexionais',
                                      label: 'Incluir divergências de morfologia flexional (plural e gênero)',
                                      checked: this.incluirDivergenciasFlexionais,
                                      onChange: this.handleChangeIncluirDivergenciasFlexionais,
                                      icon: <Groups />,
                                      sx: { pl: 9 }
                                  })
                                : null}
                            {this.renderColunaSwitch({
                                id: 'switch-list-label-exibir-processos-sem-divergencias',
                                label: 'Exibir processos sem divergências para a configuração selecionada',
                                checked: this.exibirProcessosSemDivergencias,
                                onChange: this.handleChangeExibirProcessosSemDivergencias,
                                icon: <SyncIcon />
                            })}
                        </List>
                    </Box>
                </Collapse>
            </Box>
        );
    }

    get cabecalho() {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        textAlign: 'center'
                    }}
                >
                    <Box />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Typography sx={{ justifyContent: 'center', fontWeight: 'bold', whiteSpace: 'pre-line' }}>
                            {this.titulo}
                        </Typography>
                        <Typography sx={{ justifyContent: 'center', fontStyle: 'italic' }}>
                            {this.getDataGeracao()}
                        </Typography>
                        <div style={{ margin: '0px 10px' }}>
                            <NumeroProcesso
                                dense
                                callbackOnChange={this.callbackNumeroProcesso}
                                clickLimpar={this.limparProcessoPesquisa}
                                tooltip="Número do processo a ser pesquisado na listagem"
                            />
                        </div>
                    </Box>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                margin: '10px',
                                paddingTop: '5px'
                            }}
                        >
                            <PainelFiltros />
                        </div>
                        <FiltroRapido id="filtroRapidoModalControlePermissaoSustentacao" />
                    </div>
                </Box>
                {this.renderConfiguracoes()}
            </Box>
        );
    }

    getInfoSituacaoAcordao(numeroProcesso) {
        const registro = this.sessaoStore.sessaoSelecionada.processos.find(
            (registro) =>
                NumeroProcessoUtil.formatarNumeroProcesso(registro.processo.numeroCompleto, false, true) ===
                NumeroProcessoUtil.formatarNumeroProcesso(numeroProcesso, false, true)
        );

        return <SituacaoAcordao registro={registro} />;
    }

    renderTableHead() {
        return (
            <TableHead>
                <TableRow>
                    <TableCell
                        align="center"
                        sx={{ borderTop: ESTILO_BORDA, width: LARGURA_COLUNA_TIPO }}
                    >
                        <Typography fontWeight={'bold'}>Tipo</Typography>
                    </TableCell>
                    <TableCell
                        sx={{
                            borderTop: ESTILO_BORDA,
                            borderLeft: ESTILO_BORDA,
                            width: LARGURA_COLUNA_CONTEUDO
                        }}
                        align="center"
                    >
                        <Typography fontWeight={'bold'}>Fonte</Typography>
                        <Typography
                            sx={{ display: 'block', color: grey[600], fontStyle: 'italic' }}
                            variant="caption"
                        >
                            certidão ou cadastro
                        </Typography>
                    </TableCell>
                    <TableCell
                        sx={{
                            borderTop: ESTILO_BORDA,
                            borderLeft: ESTILO_BORDA,
                            width: LARGURA_COLUNA_CONTEUDO
                        }}
                        align="center"
                    >
                        <Typography fontWeight={'bold'}>Acórdão / Voto</Typography>
                    </TableCell>
                    <TableCell
                        sx={{
                            borderTop: ESTILO_BORDA,
                            borderLeft: ESTILO_BORDA,
                            width: `calc(100% - ${LARGURA_COLUNA_TIPO} - 2 * ${LARGURA_COLUNA_CONTEUDO})`
                        }}
                        align="center"
                    >
                        <Typography fontWeight={'bold'}>Comparação</Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    }

    calcularEstatisticasMudancasTexto(htmlDiff) {
        let countInsercoes = 0;
        let countExclusoes = 0;

        if (htmlDiff) {
            countInsercoes = (htmlDiff.match(/ins class="diff/g) || []).length;
            countExclusoes = (htmlDiff.match(/del class="diff/g) || []).length;
        }

        return {
            totalInsercoes: countInsercoes,
            totalExclusoes: countExclusoes
        };
    }

    renderTableBody() {
        const corInclusao = green[600];
        const corRemocao = red[300];
        const margem = '0 1px';

        const diffStyles = `
            @page {
                size: landscape;
            }

            del.diffdel, del.diffmod {
                color: ${corRemocao};
                margin: ${margem};
            }

            ins.diffmod, ins.diffins {
                color: ${corInclusao};
                text-decoration: initial;
                margin: ${margem};
            }
        `;

        const processosVisiveis = this.processosVisiveis;
        const totalVisivel = processosVisiveis.length;
        const totalConsulta = this.totalProcessosConsulta;

        return (
            <TableBody>
                <style>{diffStyles}</style>
                {processosVisiveis.map(({ seqFixo, seqVisivel, numeroProcesso, comparacoesVisiveis }) => (
                    <Fragment key={numeroProcesso}>
                        <TableRow>
                            <TableCell
                                align="left"
                                colSpan={3}
                                sx={{ background: grey[50] }}
                            >
                                <Stack
                                    alignItems={'center'}
                                    direction={'row'}
                                    spacing={1}
                                >
                                    <Chip
                                        sx={{
                                            backgroundColor: grey[300],
                                            color: grey[800],
                                            fontWeight: 'bold',
                                            height: 22,
                                            borderRadius: '11px',
                                            '& .MuiChip-label': {
                                                px: 1.25,
                                                minWidth: 96,
                                                textAlign: 'center'
                                            }
                                        }}
                                        label={`${seqFixo} / ${totalConsulta}`}
                                        size="small"
                                        title={`Posição do processo na listagem: ${seqFixo} de ${totalConsulta}`}
                                    />
                                    <Typography sx={{ fontWeight: 'bold' }}>{numeroProcesso}</Typography>
                                    <Box>{this.getInfoSituacaoAcordao(numeroProcesso)}</Box>
                                </Stack>
                            </TableCell>
                            <TableCell
                                align="right"
                                colSpan={1}
                                sx={{ background: grey[50] }}
                            >
                                <Typography
                                    sx={{ fontStyle: 'italic' }}
                                >{`(${seqVisivel} de ${totalVisivel})`}</Typography>
                            </TableCell>
                        </TableRow>

                        {comparacoesVisiveis.map((comparacao) => (
                            <TableRow
                                key={comparacao.tipo}
                                sx={{ verticalAlign: 'top' }}
                            >
                                <TableCell
                                    sx={{
                                        background: grey[50],
                                        width: LARGURA_COLUNA_TIPO
                                    }}
                                >
                                    <Stack>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>
                                            {comparacao.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: grey[600],
                                                fontStyle: 'italic'
                                            }}
                                            variant="caption"
                                        >
                                            {this.getRotuloFonte(comparacao.fonte)}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell
                                    align="justify"
                                    sx={{ width: LARGURA_COLUNA_CONTEUDO }}
                                >
                                    {comparacao.conteudoFonte}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        borderLeft: ESTILO_BORDA,
                                        width: LARGURA_COLUNA_CONTEUDO
                                    }}
                                    align="justify"
                                >
                                    {comparacao.conteudoVoto}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        borderLeft: ESTILO_BORDA,
                                        width: `calc(100% - ${LARGURA_COLUNA_TIPO} - 2 * ${LARGURA_COLUNA_CONTEUDO})`
                                    }}
                                    align="justify"
                                >
                                    {comparacao.comparacaoNode}
                                </TableCell>
                            </TableRow>
                        ))}
                    </Fragment>
                ))}
            </TableBody>
        );
    }

    renderMsgAlerta(msg) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'center',
                    my: 2
                }}
            >
                <Warning sx={{ fill: 'darkorange' }} />
                <Typography
                    sx={{
                        color: 'darkorange',
                        fontWeight: 'bold',
                        marginLeft: '8px',
                        marginTop: '2px'
                    }}
                >
                    {msg}
                </Typography>
            </Box>
        );
    }

    renderConteudo() {
        if (this.erroCarregamento) {
            return null;
        }

        if (this.informacoesCertidaoAcordaoFiltradas?.length === 0) {
            const msg = 'Nenhum processo está disponível para geração do relatório.';
            return this.renderMsgAlerta(msg);
        }

        if (!this.exibirProcessosComDivergencias && !this.exibirProcessosSemDivergencias) {
            const msg =
                'Selecione as configurações de exibição: processos com divergências, sem divergências ou ambos.';
            return this.renderMsgAlerta(msg);
        }

        if (this.processosVisiveis.length === 0) {
            const msg = 'Nenhum processo corresponde às configurações de exibição selecionadas.';
            return this.renderMsgAlerta(msg);
        }

        return (
            <Table
                stickyHeader
                sx={{
                    borderLeft: ESTILO_BORDA,
                    borderRight: ESTILO_BORDA
                }}
            >
                {this.renderTableHead()}
                {this.renderTableBody()}
            </Table>
        );
    }

    render() {
        const carregando = this.isAindaCarregando;

        return (
            <ModalImpressao
                conteudo={
                    carregando ? (
                        <Box
                            style={{
                                display: 'flex',
                                flexGrow: 1,
                                margin: '20px',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress
                                disableShrink={true}
                                style={{ margin: '20px' }}
                            />
                            <Typography
                                color={grey[500]}
                                sx={{ margin: '25px 10px', fontStyle: 'italic' }}
                                variant="h6"
                            >
                                Carregando...
                            </Typography>
                        </Box>
                    ) : (
                        this.renderConteudo()
                    )
                }
                cabecalho={this.cabecalho}
                carregando={carregando}
                onClose={this.observableProps.onCloseModalImpressao}
                open={this.observableProps.openModalImpressao}
                titulo={this.titulo}
            />
        );
    }
}

export default inject('stores')(observer(ModalRelatorioComparacaoCertidaoAcordao));
